import React, { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  QRCode,
  Select,
  Space,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
  unstableSetRender
} from 'antd';
import 'antd/dist/reset.css';
import { apiDownload, apiGet, apiPost, clearAdminToken, setAdminToken, setLearningToken } from './api/client';
import { LearningPage, LearningDashboard } from './pages/learning-page';
import { ProbationTrackingPage } from './pages/probation-tracking-page';
import { OnlineSheetMappingPage } from './pages/online-sheet-mapping-page';
import { MailboxCreationDialog, type MailboxCreationResponse } from './pages/mailbox-creation-result';
import './styles.css';
import { FilterScopeProvider, FilteredTable } from './list-view/list-view';
import { personnelConfig, projectPersonnel } from './list-view/personnel-config';
import { adminConfig, projectAdmin, type AdminPage } from './list-view/admin-config';
import { useLocalList } from './list-view/use-local-list';
import { useAuthorizedSelection } from './list-view/use-authorized-selection';

const antdReactRoots = new WeakMap<Element | DocumentFragment, Root>();
unstableSetRender((node, container) => {
  let root = antdReactRoots.get(container);
  if (!root) {
    root = createRoot(container);
    antdReactRoots.set(container, root);
  }
  root.render(node);
  return async () => {
    root?.unmount();
    antdReactRoots.delete(container);
  };
});

const DASHBOARD_REFRESH_INTERVAL_MS = 60_000;

type OnboardingApprovalTodoSyncResult = {
  synced: number;
  generated: number;
  archived?: number;
  errors?: string[];
};

type WorkflowStage = 'PENDING_OFFER' | 'SENT_OFFER' | 'PENDING_ONBOARDING' | 'ONBOARDED' | 'ARCHIVED';
type WorkflowTransferSelection = WorkflowStage | 'CANCEL_ONBOARDING';

type ArchiveOffer = Offer & {
  materialId?: string;
  materialStatus?: string;
  arrivedAt?: string;
  dingTalkUserId?: string;
  employeeEmail?: string;
  syncExternalId?: string;
};

type Offer = {
  id: string;
  createdAt?: string;
  workflowStageUpdatedAt?: string;
  hrOwnerName?: string;
  approvalInstanceId?: string;
  candidateName: string;
  mobile: string;
  candidateEmail?: string;
  candidateEmailIsVirtual?: boolean;
  employeeType: string;
  departmentName: string;
  departmentId?: string;
  position: string;
  jobLevel?: string;
  directManager?: string;
  gender?: string;
  expectedOnboardDate: string;
  salaryPlanRaw?: string;
  status: string;
  workflowStage?: WorkflowStage | 'CANCELLED';
  workflowStageRemark?: string;
  token: string;
  expiresAt?: string;
  emailTemplateText?: string;
  templateId?: string;
  templateName?: string;
  emailSubject?: string;
  letterContent?: string;
  letterVariables?: Record<string, string>;
  emailFrom?: string;
  emailTo?: string;
  emailCc?: string[];
  emailCcPreview?: string[];
  emailSentAt?: string;
  archiveSummary?: { status: string; filename?: string; url?: string; error?: string; attempts?: number };
  candidateReadAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  approvalStatus?: string;
  approvalCurrentNodeName?: string;
  approvalCurrentHandlerName?: string;
  hrOnboardingSubmittedAt?: string;
  approvalCompletedAt?: string;
};

type OfferTemplate = {
  id: string;
  name: string;
  templateFileKey?: string;
  contentTextKey?: string;
  content?: string;
  requiredVariables?: string[];
};

type OfferNotificationTemplate = {
  id: string;
  name: string;
  subject: string;
  content: string;
};

type PublicOfferAccessState = {
  status: string;
  requiresVerification: boolean;
  remainingAttempts: number;
  maxAttempts: number;
  locked?: boolean;
  message?: string;
};

type PublicOfferVerificationResult = PublicOfferAccessState & {
  accessToken: string;
  offer: Offer;
};

type OnboardingCompletionEntry = {
  materialId: string;
  offerId: string;
  candidateName: string;
  candidateEmail?: string;
  candidateEmailIsVirtual?: boolean;
  materialStatus: string;
  onboardingUrl: string;
};

type OnboardingCompletionInvitationResult = {
  status: string;
  onboardingUrl: string;
  result?: Record<string, unknown>;
};

type Material = {
  id: string;
  createdAt?: string;
  workflowStageUpdatedAt?: string;
  hrOwnerName?: string;
  offerId: string;
  status: string;
  rejectReason?: string;
  formData: Record<string, unknown>;
  educations?: Record<string, unknown>[];
  workExperiences?: Record<string, unknown>[];
  emergencyContacts?: Record<string, unknown>[];
  familyMembers?: Record<string, unknown>[];
  attachments?: Record<string, unknown>[];
  candidateName?: string;
  mobile?: string;
  employeeType?: string;
  departmentName?: string;
  departmentId?: string;
  position?: string;
  expectedOnboardDate?: string;
  offerStatus?: string;
  workflowStage?: WorkflowStage | 'CANCELLED';
  workflowStageRemark?: string;
  reminderCount?: number;
  lastRemindedAt?: string;
  errorMessage?: string;
  syncExternalId?: string;
  syncPayload?: Record<string, unknown>;
  arrivedAt?: string;
  dingTalkUserId?: string;
  employeeEmail?: string;
  employeeEmailPreview?: string;
  accountProvisionResult?: Record<string, unknown>;
  approvalInstanceId?: string;
  approvalStatus?: string;
  approvalCurrentNodeName?: string;
  approvalCurrentHandlerName?: string;
  hrOnboardingSubmittedAt?: string;
  approvalCompletedAt?: string;
  generatedDocuments?: GeneratedOnboardingDocument[];
  updatedAt?: string;
};

type OnboardedHrmBatchPreviewItem = {
  materialId: string;
  offerId: string;
  employeeName: string;
  employeeType: string;
  hasDingTalkUserId: boolean;
  status: string;
};

type OnboardedHrmBatchPreview = {
  maxItems: number;
  total: number;
  items: OnboardedHrmBatchPreviewItem[];
};

type OnboardedHrmBatchResultItem = {
  materialId: string;
  employeeName: string;
  employeeType: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  dingTalkUserId?: string;
  errorMessage?: string;
};

type OnboardedHrmBatchResult = {
  requested: number;
  success: number;
  failed: number;
  skipped: number;
  items: OnboardedHrmBatchResultItem[];
};

type OnboardingDocumentKind =
  | 'REGISTRATION_FORM'
  | 'LABOR_CONTRACT'
  | 'SALARY_AGREEMENT'
  | 'ANTI_BRIBERY'
  | 'INTERNSHIP_AGREEMENT'
  | 'INTERN_CONFIDENTIALITY_AGREEMENT';

type GeneratedOnboardingDocument = {
  kind: OnboardingDocumentKind;
  fileName: string;
  generatedAt: string;
  generatedBy: string;
  sourceUpdatedAt: string;
};

type OnboardingDocumentPreview = {
  data: Record<string, unknown>;
  postal: {
    idAddress: PostalCodeResolution;
    currentAddress: PostalCodeResolution;
  };
  documents: { kind: OnboardingDocumentKind; name: string; format: string }[];
  generatedDocuments: GeneratedOnboardingDocument[];
};

type PostalCodeResolution = {
  status:
    | 'RESOLVED'
    | 'NOT_REQUIRED'
    | 'LOOKUP_REQUIRED'
    | 'ADDRESS_INCOMPLETE'
    | 'SERVICE_UNCONFIGURED'
    | 'SERVICE_UNAVAILABLE'
    | 'INVALID_RESULT'
    | 'SOURCE_CONFLICT'
    | 'REGION_UNRESOLVED'
    | 'DETAIL_UNRESOLVED'
    | 'AMBIGUOUS'
    | 'INDEX_UNAVAILABLE';
  code?: string;
  source: string;
  matchedAddress?: string;
  verifiedAt?: string;
  cacheHit?: boolean;
  message?: string;
};

type MenuCounts = {
  pendingOffers: number;
  sentWaitingOffers: number;
  pendingOnboarding: number;
  onboardedPeople: number;
  cancelledOnboarding: number;
  risks: number;
  archived: number;
};

type AdminUser = {
  id: string;
  name: string;
  email?: string;
  role: string;
  accessScope?: 'ALL' | 'OWN';
  dingUserId?: string;
  departmentIds?: string[];
  departmentNames?: string[];
};

type AuthStatus = {
  authenticated: boolean;
  mode: string;
  user?: AdminUser;
  expiresAt?: string;
  corpId?: string;
  loginRequired: boolean;
};

type AuthLoginResult = {
  audience?: 'LEARNING';
  token: string;
  user: AdminUser;
  expiresAt: string;
  returnPath?: string;
};

type DingTalkJsApi = {
  runtime?: {
    permission?: {
      requestAuthCode?: (options: {
        corpId: string;
        onSuccess: (result: { code?: string }) => void;
        onFail: (error: unknown) => void;
      }) => void;
    };
  };
};

declare global {
  interface Window {
    dd?: DingTalkJsApi;
  }
}

export function App() {
  const path = window.location.pathname;
  if (path === '/learning') return <LearningPage />;
  if (path.startsWith('/offer/')) return <PublicOfferPage token={path.split('/').pop()!} />;
  if (path.startsWith('/onboarding/')) return <PublicOnboardingPage token={path.split('/').pop()!} />;
  if (path === '/auth/sso/callback') return <RosettaAuthCallback />;
  if (path === '/auth/dingtalk/callback') return <DingTalkAuthCallback />;
  return <AuthGate />;
}

function AuthGate() {
  const [checking, setChecking] = useState(true);
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [error, setError] = useState('');
  const authGeneration = useRef(0);

  const loadStatus = async () => {
    const request = ++authGeneration.current;
    setChecking(true);
    setAuth(null);
    try {
      setError('');
      const data = await apiGet<AuthStatus>('/auth/status');
      if (request !== authGeneration.current) return;
      setAuth(data);
    } catch (nextError) {
      if (request !== authGeneration.current) return;
      setError(humanizeErrorMessage((nextError as Error).message));
      setAuth({ authenticated: false, mode: 'rosetta', loginRequired: true });
    } finally {
      if (request === authGeneration.current) setChecking(false);
    }
  };

  useEffect(() => {
    void loadStatus();
    const onExpired = () => void loadStatus();
    const onRevoked = () => {
      authGeneration.current++;
      setAuth(null);
      setChecking(false);
      setError('访问权限已变化，请重新登录');
    };
    window.addEventListener('stardust-auth-expired', onExpired);
    window.addEventListener('stardust-permissions-revoked', onRevoked);
    return () => {
      authGeneration.current++;
      window.removeEventListener('stardust-auth-expired', onExpired);
      window.removeEventListener('stardust-permissions-revoked', onRevoked);
    };
  }, []);

  if (checking) return <AuthShell title="正在验证登录状态..." />;
  if (!auth?.authenticated || !auth.user) {
    return <DingTalkLoginPage error={error} mode={auth?.mode ?? 'rosetta'} corpId={auth?.corpId} />;
  }
  return (
    <FilterScopeProvider scopeKey={`${auth.user.id}:${auth.user.role}:${auth.user.accessScope ?? ''}`}>
      <HrApp currentUser={auth.user} onLogout={() => void loadStatus()} />
    </FilterScopeProvider>
  );
}

function DingTalkAuthCallback() {
  const [messageText, setMessageText] = useState('正在完成钉钉登录...');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') ?? '';
    const state = params.get('state') ?? '';
    if (!code || !state) {
      setError('钉钉登录参数缺失，请返回重新登录');
      return;
    }
    void apiPost<AuthLoginResult>('/auth/dingtalk/callback', { code, state })
      .then((result) => {
        if (result.audience === 'LEARNING') setLearningToken(result.token);
        else setAdminToken(result.token);
        setMessageText('登录成功，正在进入系统...');
        window.location.replace(result.returnPath || '/');
      })
      .catch((nextError) => {
        clearAdminToken();
        setError(humanizeErrorMessage((nextError as Error).message));
      });
  }, []);

  return <AuthShell title={messageText} error={error} />;
}

function RosettaAuthCallback() {
  const [messageText, setMessageText] = useState('正在完成 Rosetta SSO 登录...');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') ?? '';
    const state = params.get('state') ?? '';
    if (!code || !state) {
      setError('Rosetta SSO 登录参数缺失，请返回重新登录');
      return;
    }
    void apiPost<AuthLoginResult>('/auth/sso/callback', { code, state })
      .then((result) => {
        setAdminToken(result.token);
        setMessageText('登录成功，正在进入系统...');
        window.location.replace(result.returnPath || '/');
      })
      .catch((nextError) => {
        clearAdminToken();
        setError(humanizeErrorMessage((nextError as Error).message));
      });
  }, []);

  return <AuthShell title={messageText} error={error} />;
}

function DingTalkLoginPage({ error, mode, corpId }: { error?: string; mode: string; corpId?: string }) {
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [autoLoginTried, setAutoLoginTried] = useState(false);
  const [autoLoginFailed, setAutoLoginFailed] = useState(false);
  const [autoLoginMessage, setAutoLoginMessage] = useState('');
  const [passwordLoginForm] = Form.useForm<{ email: string; password: string }>();
  const [messageApi, holder] = message.useMessage();

  const loginWithCorpAuthCode = async () => {
    if (!corpId) {
      setAutoLoginMessage('钉钉企业 CorpId 未配置，无法自动登录。');
      setAutoLoginFailed(true);
      return;
    }
    try {
      setLoading(true);
      setAutoLoginFailed(false);
      setAutoLoginMessage('正在通过钉钉自动登录...');
      const dd = await loadDingTalkJsApi();
      const code = await requestDingTalkAuthCode(dd, corpId);
      const result = await apiPost<AuthLoginResult>('/auth/dingtalk/code', { code });
      setAdminToken(result.token);
      window.location.replace('/');
    } catch (nextError) {
      setAutoLoginFailed(true);
      setAutoLoginMessage(`${humanizeErrorMessage((nextError as Error).message)}。可改用扫码登录。`);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'dev' || autoLoginTried || !isDingTalkClient()) return;
    setAutoLoginTried(true);
    void loginWithCorpAuthCode();
  }, [mode, autoLoginTried, corpId]);

  const startLogin = async (loginMode?: 'oauth2' | 'sns_qr') => {
    try {
      setLoading(true);
      const returnPath = `${window.location.pathname}${window.location.search}`;
      const modeQuery = loginMode ? `&mode=${encodeURIComponent(loginMode)}` : '';
      const result = await apiGet<{ loginUrl: string }>(
        `/auth/dingtalk/login-url?returnPath=${encodeURIComponent(returnPath)}${modeQuery}`
      );
      window.location.href = result.loginUrl;
    } catch (nextError) {
      messageApi.error(humanizeErrorMessage((nextError as Error).message));
    } finally {
      setLoading(false);
    }
  };
  const startRosettaLogin = async () => {
    try {
      setLoading(true);
      const returnPath = `${window.location.pathname}${window.location.search}`;
      const result = await apiGet<{ loginUrl: string }>(
        `/auth/sso/login-url?returnPath=${encodeURIComponent(returnPath)}`
      );
      window.location.href = result.loginUrl;
    } catch (nextError) {
      messageApi.error(humanizeErrorMessage((nextError as Error).message));
    } finally {
      setLoading(false);
    }
  };
  const startDevLogin = async () => {
    try {
      setLoading(true);
      const result = await apiPost<AuthLoginResult>('/auth/dev-login', {});
      setAdminToken(result.token);
      window.location.replace('/');
    } catch (nextError) {
      messageApi.error(humanizeErrorMessage((nextError as Error).message));
    } finally {
      setLoading(false);
    }
  };
  const startPasswordLogin = async (values: { email: string; password: string }) => {
    try {
      setPasswordLoading(true);
      const result = await apiPost<AuthLoginResult>('/auth/password-login', values);
      setAdminToken(result.token);
      window.location.replace('/');
    } catch (nextError) {
      messageApi.error(humanizeErrorMessage((nextError as Error).message));
    } finally {
      setPasswordLoading(false);
    }
  };
  const inDingTalkClient = isDingTalkClient();
  return (
    <AuthShell
      title="Stardust 入职"
      subtitle={
        mode === 'rosetta'
          ? '请使用公司钉钉身份通过 Rosetta SSO 登录，系统仅允许已授权的人力资源团队访问。'
          : inDingTalkClient
            ? '正在使用钉钉自动登录，系统仅允许人力资源团队访问。'
            : '请使用钉钉或账号密码登录，系统仅允许人力资源团队访问。'
      }
      error={error}
      extra={
        <Space direction="vertical" className="full">
          {autoLoginMessage && (
            <Alert type={autoLoginMessage.includes('正在') ? 'info' : 'warning'} message={autoLoginMessage} showIcon />
          )}
          {mode === 'dev' && (
            <Space direction="vertical" className="full">
              <Button type="primary" size="large" loading={loading} onClick={startDevLogin}>
                本地开发登录
              </Button>
              <Typography.Text type="secondary">当前为开发登录模式，仅用于本地检查。</Typography.Text>
            </Space>
          )}
          {mode === 'rosetta' && (
            <Button type="primary" size="large" loading={loading} onClick={startRosettaLogin}>
              使用 Rosetta SSO 登录
            </Button>
          )}
          {mode === 'dingtalk' &&
            (inDingTalkClient ? (
              <Space direction="vertical" className="full">
                <Button
                  type="primary"
                  size="large"
                  loading={loading && !autoLoginFailed}
                  onClick={loginWithCorpAuthCode}
                >
                  钉钉自动登录
                </Button>
                {autoLoginFailed && (
                  <Button size="large" loading={loading} onClick={() => startLogin('sns_qr')}>
                    扫码登录
                  </Button>
                )}
              </Space>
            ) : (
              <Button type="primary" size="large" loading={loading} onClick={() => startLogin()}>
                钉钉登录
              </Button>
            ))}
          <div className="auth-divider">账号密码登录</div>
          <Form
            form={passwordLoginForm}
            layout="vertical"
            className="password-login-form"
            autoComplete="off"
            onFinish={startPasswordLogin}
          >
            <Form.Item name="email" label="账号" rules={[{ required: true, message: '请输入账号' }]}>
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={passwordLoading} block>
              登录
            </Button>
          </Form>
        </Space>
      }
    >
      {holder}
    </AuthShell>
  );
}

function isDingTalkClient() {
  return /DingTalk|AliApp\(DingTalk/i.test(window.navigator.userAgent);
}

function loadDingTalkJsApi() {
  if (window.dd?.runtime?.permission?.requestAuthCode) return Promise.resolve(window.dd);
  return new Promise<DingTalkJsApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-stardust-dingtalk-jsapi]');
    if (existing) {
      existing.addEventListener(
        'load',
        () => (window.dd ? resolve(window.dd) : reject(new Error('钉钉 JSAPI 加载失败'))),
        {
          once: true
        }
      );
      existing.addEventListener('error', () => reject(new Error('钉钉 JSAPI 加载失败')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://g.alicdn.com/dingding/dingtalk-jsapi/3.0.36/dingtalk.open.js';
    script.async = true;
    script.dataset.stardustDingtalkJsapi = 'true';
    script.onload = () => (window.dd ? resolve(window.dd) : reject(new Error('钉钉 JSAPI 加载失败')));
    script.onerror = () => reject(new Error('钉钉 JSAPI 加载失败'));
    document.head.appendChild(script);
  });
}

function requestDingTalkAuthCode(dd: DingTalkJsApi, corpId: string) {
  return new Promise<string>((resolve, reject) => {
    const requestAuthCode = dd.runtime?.permission?.requestAuthCode;
    if (!requestAuthCode) {
      reject(new Error('当前环境不支持钉钉自动登录，请在钉钉客户端内打开应用'));
      return;
    }
    requestAuthCode({
      corpId,
      onSuccess: (result) => {
        if (result.code) resolve(result.code);
        else reject(new Error('钉钉未返回登录授权码'));
      },
      onFail: (nextError) => {
        reject(new Error(readDingTalkError(nextError)));
      }
    });
  });
}

function readDingTalkError(error: unknown) {
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    return String(record.errorMessage ?? record.errmsg ?? record.message ?? '钉钉自动登录失败');
  }
  return String(error || '钉钉自动登录失败');
}

function AuthShell({
  title,
  subtitle,
  error,
  extra,
  children
}: {
  title: string;
  subtitle?: string;
  error?: string;
  extra?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <main className="auth-page">
      <section className="auth-panel">
        <Typography.Title level={2}>{title}</Typography.Title>
        {subtitle && <Typography.Paragraph type="secondary">{subtitle}</Typography.Paragraph>}
        {error && <Alert type="error" message={error} showIcon />}
        {extra}
        {children}
      </section>
    </main>
  );
}

function HrApp({ currentUser, onLogout }: { currentUser: AdminUser; onLogout: () => void }) {
  const [tab, setTab] = useState('offer-overview');
  const [bootstrapped, setBootstrapped] = useState(false);
  const hasAdminAccess = currentUser.role === 'ADMIN' || currentUser.accessScope === 'ALL';
  const [counts, setCounts] = useState<MenuCounts>({
    pendingOffers: 0,
    sentWaitingOffers: 0,
    pendingOnboarding: 0,
    onboardedPeople: 0,
    cancelledOnboarding: 0,
    risks: 0,
    archived: 0
  });
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [offers, materials, risks] = await Promise.all([
          apiGet<Offer[]>('/offers'),
          apiGet<Material[]>('/hr/onboarding'),
          apiGet<Record<string, unknown>[]>('/hr/risks')
        ]);
        setCounts({
          pendingOffers: offers.filter((offer) => offerWorkflowStage(offer) === 'PENDING_OFFER').length,
          sentWaitingOffers: offers.filter((offer) => offerWorkflowStage(offer) === 'SENT_OFFER').length,
          pendingOnboarding: materials.filter((item) => isPendingOnboardingItem(item)).length,
          onboardedPeople: materials.filter((item) => isOnboardedPeopleItem(item)).length,
          cancelledOnboarding: materials.filter((item) => isCancelledOnboardingItem(item)).length,
          risks: risks.filter((risk) => risk.status === 'PENDING').length,
          archived: offers.length
        });
      } catch {
        // The page should stay usable even if one counter endpoint is temporarily unavailable.
      }
    };
    const bootstrap = async () => {
      await loadCounts();
      setBootstrapped(true);
    };
    void bootstrap();
    const timer = window.setInterval(() => void loadCounts(), DASHBOARD_REFRESH_INTERVAL_MS);
    const refreshOnFocus = () => void loadCounts();
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, []);
  useEffect(() => {
    if (!hasAdminAccess && isAdminOnlyTab(tab)) setTab('offer-overview');
  }, [hasAdminAccess, tab]);
  if (!bootstrapped) {
    return (
      <Layout className="shell">
        <Layout.Sider width={260} className="sider">
          <Typography.Title level={4}>Stardust 入职</Typography.Title>
        </Layout.Sider>
        <Layout.Content className="content">
          <section>正在加载本地数据...</section>
        </Layout.Content>
      </Layout>
    );
  }
  return (
    <Layout className="shell">
      <Layout.Sider width={260} className="sider">
        <Typography.Title level={4}>Stardust 入职</Typography.Title>
        <div className="admin-user-panel">
          <Typography.Text>{currentUser.name}</Typography.Text>
          <Typography.Text type="secondary">{hasAdminAccess ? '全量管理员' : '招聘人员'}</Typography.Text>
          <Button
            size="small"
            onClick={async () => {
              await apiPost('/auth/logout').catch(() => undefined);
              clearAdminToken();
              onLogout();
            }}
          >
            退出登录
          </Button>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[tab]}
          openKeys={[
            'offer-management',
            'onboarding-management',
            'probation-management',
            'template-management',
            'mapping-management',
            'other-management'
          ]}
          onClick={(item) => setTab(item.key)}
          items={[
            {
              key: 'offer-management',
              label: 'Offer管理',
              children: [
                { key: 'offer-overview', label: menuLabel('待发Offer', counts.pendingOffers) },
                { key: 'sent-offers', label: menuLabel('已发Offer', counts.sentWaitingOffers) }
              ]
            },
            {
              key: 'onboarding-management',
              label: '入职管理',
              children: [
                { key: 'pending-onboarding-people', label: menuLabel('待入职人员', counts.pendingOnboarding) },
                { key: 'onboarded-people', label: menuLabel('已入职人员', counts.onboardedPeople) },
                ...(hasAdminAccess ? [{ key: 'signature-documents', label: '签署文件' }] : []),
                { key: 'cancelled-onboarding-people', label: menuLabel('取消入职人员', counts.cancelledOnboarding) }
              ]
            },
            {
              key: 'probation-management',
              label: '试用期管理',
              children: [
                { key: 'probation-tracking', label: '试用期追踪' },
                { key: 'learning-dashboard', label: '新人学习追踪' }
              ]
            },
            ...(hasAdminAccess
              ? [
                  {
                    key: 'template-management',
                    label: '模板管理',
                    children: [
                      { key: 'offer-page-templates', label: '邮件页面模板管理' },
                      { key: 'offer-notification-templates', label: '邮件通知模板管理' },
                      { key: 'offer-file-templates', label: 'Offer文件模板管理' }
                    ]
                  },
                  {
                    key: 'mapping-management',
                    label: '映射管理',
                    children: [
                      { key: 'smart-hrm-mapping', label: '智能人事映射' },
                      { key: 'roster-mapping', label: '花名册表映射' },
                      { key: 'employee-lifecycle-mapping', label: '入转调离表映射' }
                    ]
                  }
                ]
              : []),
            {
              key: 'other-management',
              label: '其他',
              children: [
                { key: 'risks', label: menuLabel('异常提醒', counts.risks) },
                { key: 'workflow-archive', label: menuLabel('流程归档', counts.archived) },
                ...(hasAdminAccess ? [{ key: 'logs', label: '操作日志' }] : [])
              ]
            }
          ]}
        />
      </Layout.Sider>
      <Layout.Content className="content">
        {tab === 'offer-overview' && <OfferManagement mode="pending" />}
        {tab === 'sent-offers' && <OfferManagement mode="sent" />}
        {hasAdminAccess && tab === 'offer-page-templates' && <OfferPageTemplateManagement />}
        {hasAdminAccess && tab === 'offer-notification-templates' && <OfferNotificationTemplateManagement />}
        {hasAdminAccess && tab === 'offer-file-templates' && <OfferFileTemplateManagement />}
        {tab === 'pending-onboarding-people' && <OnboardingManagement canViewMaterialDetail={hasAdminAccess} />}
        {tab === 'onboarded-people' && <OnboardedPeople canManageWorkflow canViewMaterialDetail={hasAdminAccess} />}
        {hasAdminAccess && tab === 'signature-documents' && <SignatureDocumentsPage />}
        {tab === 'cancelled-onboarding-people' && <CancelledOnboardingPeople canManageSensitive={hasAdminAccess} />}
        {tab === 'probation-tracking' && <ProbationTrackingPage />}
        {tab === 'learning-dashboard' && <LearningDashboard admin={hasAdminAccess} />}
        {tab === 'risks' && <RiskList />}
        {tab === 'workflow-archive' && <WorkflowArchive />}
        {hasAdminAccess && tab === 'smart-hrm-mapping' && <SmartHrmMappingPage />}
        {hasAdminAccess && tab === 'roster-mapping' && <OnlineSheetMappingPage target="ROSTER" />}
        {hasAdminAccess && tab === 'employee-lifecycle-mapping' && (
          <OnlineSheetMappingPage target="EMPLOYEE_LIFECYCLE" />
        )}
        {hasAdminAccess && tab === 'logs' && <LogPage />}
      </Layout.Content>
    </Layout>
  );
}

function isAdminOnlyTab(tab: string) {
  return [
    'offer-page-templates',
    'offer-notification-templates',
    'offer-file-templates',
    'signature-documents',
    'smart-hrm-mapping',
    'roster-mapping',
    'employee-lifecycle-mapping',
    'logs'
  ].includes(tab);
}

function menuLabel(text: string, count: number) {
  return (
    <span className="menu-badge-label">
      <span>{text}</span>
      <Badge count={count} size="small" />
    </span>
  );
}

function OfferPageTemplateManagement() {
  const [template, setTemplate] = useState<OfferNotificationTemplate | null>(null);
  const [messageApi, holder] = message.useMessage();
  useEffect(() => void apiGet<OfferNotificationTemplate>('/admin/offer-notification-template').then(setTemplate), []);
  if (!template) return <section>加载中...</section>;
  const sampleOffer: Offer = {
    id: 'sample',
    candidateName: '候选人姓名',
    mobile: '13800000000',
    employeeType: '正式',
    departmentName: '示例部门',
    position: '示例岗位',
    expectedOnboardDate: '2026-07-14',
    status: 'SENT',
    token: 'sample-token',
    expiresAt: '2026-07-14T23:59:59.999Z'
  };
  const sampleSubject = fillOfferNotificationText(template.subject, sampleOffer);
  return (
    <section>
      {holder}
      <PageTitle
        title="邮件页面模板管理"
        extra={
          <Button
            type="primary"
            onClick={async () => {
              const saved = await apiPost<OfferNotificationTemplate>('/admin/offer-notification-template', template);
              setTemplate(saved);
              messageApi.success('已保存邮件页面模板');
            }}
          >
            保存
          </Button>
        }
      />
      <div className="page-template-editor">
        <Form layout="vertical" className="page-template-form">
          <Form.Item label="Logo">
            <div className="logo-field">
              <img src="/logo-stardust.svg" alt="STARDUST" />
            </div>
          </Form.Item>
          <Form.Item label="模板名称">
            <Input value={template.name} onChange={(event) => setTemplate({ ...template, name: event.target.value })} />
          </Form.Item>
          <Form.Item label="邮件主题">
            <Input
              value={template.subject}
              onChange={(event) => setTemplate({ ...template, subject: event.target.value })}
            />
          </Form.Item>
          <Form.Item label="邮件内容">
            <Input.TextArea
              rows={16}
              value={template.content}
              onChange={(event) => setTemplate({ ...template, content: event.target.value })}
            />
          </Form.Item>
        </Form>
        <div className="page-template-preview">
          <EmailPreviewCard
            offer={sampleOffer}
            template={template}
            subject={sampleSubject}
            actions={
              <Space className="email-actions">
                <Button type="primary">接受offer</Button>
                <Button danger>拒绝offer</Button>
              </Space>
            }
          />
        </div>
      </div>
    </section>
  );
}

function OfferNotificationTemplateManagement() {
  const [template, setTemplate] = useState<OfferNotificationTemplate | null>(null);
  const [messageApi, holder] = message.useMessage();
  useEffect(() => void apiGet<OfferNotificationTemplate>('/admin/offer-notification-template').then(setTemplate), []);
  if (!template) return <section>加载中...</section>;
  return (
    <section>
      {holder}
      <PageTitle
        title="邮件通知模板管理"
        extra={
          <Button
            type="primary"
            onClick={async () => {
              const saved = await apiPost<OfferNotificationTemplate>('/admin/offer-notification-template', template);
              setTemplate(saved);
              messageApi.success('已保存邮件通知模板');
            }}
          >
            保存
          </Button>
        }
      />
      <Space direction="vertical" className="full">
        <Form layout="vertical">
          <Form.Item label="模板名称">
            <Input value={template.name} onChange={(event) => setTemplate({ ...template, name: event.target.value })} />
          </Form.Item>
          <Form.Item label="邮件主题">
            <Input
              value={template.subject}
              onChange={(event) => setTemplate({ ...template, subject: event.target.value })}
            />
          </Form.Item>
        </Form>
        <Input.TextArea
          rows={16}
          value={template.content}
          onChange={(event) => setTemplate({ ...template, content: event.target.value })}
        />
        <Alert message="可用变量：【姓名】、【候选人姓名】、【岗位名称】、【职位】、【入职日期】、【预计入职日期】、【日期（发offer3天内）】、【时间】、【发offer日期】。正文中的《录用通知函》会在发送时自动关联候选人的Offer页面。" />
      </Space>
    </section>
  );
}

function OfferFileTemplateManagement() {
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [messageApi, holder] = message.useMessage();
  useEffect(
    () =>
      void apiGet<OfferTemplate[]>('/admin/offer-templates').then((items) => {
        setTemplates(items);
        setSelectedTemplateId(items[0]?.id ?? '');
      }),
    []
  );
  const updateTemplate = (id: string, changes: Partial<OfferTemplate>) => {
    setTemplates((items) => items.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  };
  const selectedTemplate = templates.find((item) => item.id === selectedTemplateId);
  return (
    <section>
      {holder}
      <PageTitle
        title="Offer文件模板管理"
        extra={
          <Button
            type="primary"
            onClick={async () => {
              await apiPost('/admin/offer-templates', { items: templates });
              messageApi.success('已保存文件模板');
            }}
          >
            保存
          </Button>
        }
      />
      <div className="template-editor">
        <div className="template-list">
          <FilteredTable<OfferTemplate>
            config={adminConfig('templates')}
            project={(row, index) => projectAdmin('templates', row, index)}
            rowKey="id"
            dataSource={templates}
            pagination={false}
            columns={[
              {
                title: '模板',
                render: (_, template) => (
                  <Button
                    type={template.id === selectedTemplateId ? 'primary' : 'default'}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    {template.name}
                  </Button>
                )
              }
            ]}
          />
        </div>
        {selectedTemplate && (
          <Space direction="vertical" className="template-detail">
            <Form layout="vertical">
              <Form.Item label="模板名称">
                <Input
                  value={selectedTemplate.name}
                  onChange={(event) => updateTemplate(selectedTemplate.id, { name: event.target.value })}
                />
              </Form.Item>
              <Form.Item label="模板正文">
                <Input.TextArea
                  rows={16}
                  value={selectedTemplate.content ?? ''}
                  onChange={(event) => updateTemplate(selectedTemplate.id, { content: event.target.value })}
                />
              </Form.Item>
              <Form.Item label="可用变量">
                <Input.TextArea
                  rows={3}
                  value={(selectedTemplate.requiredVariables ?? []).join('\n')}
                  onChange={(event) =>
                    updateTemplate(selectedTemplate.id, {
                      requiredVariables: event.target.value
                        .split('\n')
                        .map((text) => text.trim())
                        .filter(Boolean)
                    })
                  }
                />
              </Form.Item>
            </Form>
          </Space>
        )}
      </div>
    </section>
  );
}

function OfferManagement({ mode }: { mode: 'pending' | 'sent' }) {
  const { items: offers, setItems: setOffers, load, error: listError } = useLocalList<Offer>('/offers');
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [notificationTemplate, setNotificationTemplate] = useState<OfferNotificationTemplate | null>(null);
  const [sendOffer, setSendOffer] = useAuthorizedSelection<Offer>(offers);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [letterContent, setLetterContent] = useState('');
  const [letterVariables, setLetterVariables] = useState<Record<string, string>>({});
  const [emailCc, setEmailCc] = useState<string[]>([]);
  const [syncError, setSyncError] = useState('');
  const [syncingTodos, setSyncingTodos] = useState(false);
  const [confirmApprovalSync, setConfirmApprovalSync] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offerActionLoading, setOfferActionLoading] = useState('');
  const [messageApi, holder] = message.useMessage();
  const loadSendConfiguration = async () => {
    const configuration = await apiGet<{
      templates: OfferTemplate[];
      notificationTemplate: OfferNotificationTemplate;
    }>('/offers/send-configuration');
    setTemplates(configuration.templates);
    setNotificationTemplate(configuration.notificationTemplate);
    return configuration;
  };
  useEffect(() => {
    void loadSendConfiguration().catch((error) => setSyncError((error as Error).message));
    if (mode !== 'pending') return undefined;

    const timer = window.setInterval(() => void load().catch(() => undefined), DASHBOARD_REFRESH_INTERVAL_MS);
    const refreshOnFocus = () => void load().catch(() => undefined);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [mode]);

  const syncTodos = async () => {
    if (syncingTodos) return;
    try {
      setSyncingTodos(true);
      const result = await apiPost<OnboardingApprovalTodoSyncResult>('/approvals/sync-my-onboarding-todos', {
        force: true
      });
      const messageText = `已刷新办理入职待办，同步 ${result.synced} 条，生成 ${result.generated} 个Offer，归档 ${
        result.archived ?? 0
      } 个过期待发Offer`;
      if (result.errors?.length) {
        setSyncError(result.errors.join('\n'));
        messageApi.warning(messageText);
      } else {
        setSyncError('');
        messageApi.success(messageText);
      }
      await load();
    } catch (error) {
      const messageText = humanizeErrorMessage((error as Error).message);
      setSyncError(messageText);
      messageApi.error(messageText);
    } finally {
      setSyncingTodos(false);
    }
  };
  const visibleOffers =
    mode === 'pending'
      ? offers.filter((offer) => offerWorkflowStage(offer) === 'PENDING_OFFER')
      : offers.filter((offer) => offerWorkflowStage(offer) === 'SENT_OFFER');
  const selectedTemplate = templates.find((template) => template.id === (selectedTemplateId || sendOffer?.templateId));
  const editableLetterTemplate = selectedTemplate?.content ?? '';
  const finalLetterContent = editableLetterTemplate
    ? fillLetterTemplate(editableLetterTemplate, letterVariables)
    : letterContent;
  const runOfferAction = async (loadingKey: string, actionName: string, task: () => Promise<void>) => {
    try {
      setOfferActionLoading(loadingKey);
      await task();
    } catch (error) {
      messageApi.error(`${actionName}失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setOfferActionLoading('');
    }
  };
  const openOfferModal = async (offer: Offer) => {
    const fallbackTemplateId = offer.templateId ?? '';
    setSelectedTemplateId(fallbackTemplateId);
    setLetterContent(offer.letterContent ?? '');
    setLetterVariables(withGenderSalutation(toStringRecord(offer.letterVariables ?? {}), offer.gender));
    setEmailCc(offerEmailCcValue(offer));
    setSendOffer(offer);
    try {
      setOfferActionLoading(`prepare-${offer.id}`);
      await loadSendConfiguration();
      if (offer.status !== 'READY_TO_SEND') {
        setEmailCc(await loadEmailCcPreview(offer));
        return;
      }
      const refreshed = await apiPost<Offer>(`/offers/${offer.id}/regenerate`, {
        overrides: { templateId: fallbackTemplateId }
      });
      setOffers((items) => items.map((item) => (item.id === refreshed.id ? refreshed : item)));
      setSendOffer(refreshed);
      setSelectedTemplateId(refreshed.templateId ?? fallbackTemplateId);
      setLetterContent(refreshed.letterContent ?? '');
      setLetterVariables(withGenderSalutation(toStringRecord(refreshed.letterVariables ?? {}), refreshed.gender));
      setEmailCc(await loadEmailCcPreview(refreshed));
    } catch (error) {
      messageApi.error(`刷新最新模板失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setOfferActionLoading('');
    }
  };
  const changeSendTemplate = async (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!sendOffer || sendOffer.status !== 'READY_TO_SEND') return;
    try {
      setOfferActionLoading(`prepare-${sendOffer.id}`);
      await loadSendConfiguration();
      const refreshed = await apiPost<Offer>(`/offers/${sendOffer.id}/regenerate`, {
        overrides: { templateId }
      });
      setOffers((items) => items.map((item) => (item.id === refreshed.id ? refreshed : item)));
      setSendOffer(refreshed);
      setSelectedTemplateId(refreshed.templateId ?? templateId);
      setLetterContent(refreshed.letterContent ?? '');
      setLetterVariables(withGenderSalutation(toStringRecord(refreshed.letterVariables ?? {}), refreshed.gender));
      setEmailCc(await loadEmailCcPreview(refreshed));
    } catch (error) {
      messageApi.error(`切换模板失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setOfferActionLoading('');
    }
  };
  async function loadEmailCcPreview(offer: Offer) {
    if (offer.status !== 'READY_TO_SEND') return offerEmailCcValue(offer);
    try {
      const preview = await apiGet<{ cc: string[]; warnings?: string[] }>(`/offers/${offer.id}/email-cc-preview`);
      if (preview.warnings?.length) messageApi.warning(preview.warnings.join('；'));
      return normalizeEmailTags(preview.cc ?? []);
    } catch (error) {
      messageApi.warning(`读取默认抄送失败：${humanizeErrorMessage((error as Error).message)}`);
      return offerEmailCcValue(offer);
    }
  }
  const currentEmailSubject =
    sendOffer?.status === 'READY_TO_SEND' && notificationTemplate
      ? fillOfferNotificationText(notificationTemplate.subject, sendOffer)
      : (sendOffer?.emailSubject ?? `${sendOffer?.candidateName ?? ''}欢迎加入星尘数据`);
  const currentEmailBody =
    sendOffer?.status === 'READY_TO_SEND' && notificationTemplate
      ? fillOfferNotificationText(notificationTemplate.content, sendOffer)
      : (sendOffer?.emailTemplateText ?? '');

  return (
    <section>
      {holder}
      <PageTitle
        title={mode === 'pending' ? '待发Offer' : '已发Offer'}
        extra={
          mode === 'pending' ? (
            <Space>
              <Button onClick={() => void load()}>刷新</Button>
              <Button onClick={() => setConfirmApprovalSync(true)} loading={syncingTodos} disabled={syncingTodos}>
                同步钉钉审批
              </Button>
            </Space>
          ) : undefined
        }
      />
      <Modal
        title="确认同步钉钉审批"
        open={confirmApprovalSync}
        okText="确认同步"
        cancelText="取消"
        confirmLoading={syncingTodos}
        onCancel={() => setConfirmApprovalSync(false)}
        onOk={async () => {
          await syncTodos();
          setConfirmApprovalSync(false);
        }}
      >
        此操作将调用钉钉读取当前审批待办，并更新对应 Offer 状态。日常查看请使用“刷新”，只读取本地数据。
      </Modal>
      {mode === 'pending' && syncError && (
        <Alert
          className="sync-error-alert"
          type="warning"
          showIcon
          message="钉钉审批同步失败"
          description={syncError}
        />
      )}
      <FilteredTable
        error={listError}
        config={personnelConfig(mode === 'pending' ? 'pending-offer' : 'sent-offer')}
        project={(row, index) =>
          projectPersonnel(row, index, { status: offerStatusText(row.status), approval: approvalProgressText(row) })
        }
        className="data-table"
        rowKey="id"
        dataSource={visibleOffers}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', width: 160, render: renderNameText },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value))
          },
          {
            title: '候选人邮箱',
            dataIndex: 'candidateEmail',
            width: 240,
            render: (value, offer) => (
              <Space direction="vertical" size={2}>
                {renderTableText(value || '-')}
                {offer.candidateEmailIsVirtual && <Tag color="blue">虚拟</Tag>}
              </Space>
            )
          },
          { title: '职位', dataIndex: 'position', width: 220, render: renderTableText },
          { title: '员工类型', dataIndex: 'employeeType', width: 110, render: renderTableText },
          { title: '岗位职级', dataIndex: 'jobLevel', width: 110, render: (value) => renderTableText(value || '-') },
          {
            title: '直属主管',
            dataIndex: 'directManager',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          { title: '预计入职时间', dataIndex: 'expectedOnboardDate', width: 140, render: renderTableText },
          {
            title: '试用期薪资（元）',
            width: 150,
            render: (_, offer) => renderTableText(offer.letterVariables?.['试用期薪资'] || '-')
          },
          {
            title: '转正薪资（元）',
            width: 150,
            render: (_, offer) => renderTableText(offer.letterVariables?.['转正后薪资'] || '-')
          },
          {
            title: '社保公积金基数（元）',
            width: 180,
            render: (_, offer) => renderTableText(offer.letterVariables?.['社保基数'] || '-')
          },
          ...(mode === 'sent'
            ? [
                {
                  title: '发送时间',
                  dataIndex: 'emailSentAt',
                  width: 180,
                  render: (value: string) => renderTableText(value || '-')
                },
                {
                  title: '收件邮箱',
                  dataIndex: 'emailTo',
                  width: 220,
                  render: (value: string) => renderTableText(value || '-')
                },
                {
                  title: '接收状态',
                  width: 130,
                  render: (_: unknown, offer: Offer) => <Tag>{offerReceiptText(offer)}</Tag>
                },
                {
                  title: 'PDF归档',
                  width: 240,
                  render: (_: unknown, offer: Offer) => (
                    <Space direction="vertical" size={2}>
                      <Tag color={offer.archiveSummary?.status === 'SUCCEEDED' ? 'green' : undefined}>
                        {offerArchiveStatusText(offer.archiveSummary?.status)}
                      </Tag>
                      {offer.archiveSummary?.url && (
                        <a href={offer.archiveSummary.url} target="_blank" rel="noreferrer">
                          查看归档PDF
                        </a>
                      )}
                      {offer.archiveSummary?.error && (
                        <Typography.Text type="secondary">{offer.archiveSummary.error}</Typography.Text>
                      )}
                      {['FAILED', 'UNKNOWN'].includes(offer.archiveSummary?.status ?? '') && (
                        <Button
                          size="small"
                          onClick={() =>
                            Modal.confirm({
                              title: '仅重试PDF归档',
                              content: '只核对或上传该次发送的PDF，不会重发Offer邮件，也不会覆盖已有文件。',
                              onOk: async () => {
                                await apiPost(`/offers/${offer.id}/archive/retry`, { confirmed: true });
                                messageApi.success('归档重试已排队');
                                await load();
                              }
                            })
                          }
                        >
                          重试归档
                        </Button>
                      )}
                    </Space>
                  )
                }
              ]
            : []),
          { title: '状态', dataIndex: 'status', width: 120, render: (status) => <Tag>{offerStatusText(status)}</Tag> },
          {
            title: '操作',
            width: 260,
            render: (_, offer) => (
              <Space wrap>
                <Button
                  loading={offerActionLoading === `prepare-${offer.id}`}
                  onClick={() => void openOfferModal(offer)}
                >
                  {offerActionText(offer.status)}
                </Button>
                <Button
                  loading={offerActionLoading === `withdraw-${offer.id}`}
                  onClick={() =>
                    runOfferAction(`withdraw-${offer.id}`, '撤回Offer', async () => {
                      await apiPost(`/offers/${offer.id}/withdraw`);
                      await load();
                    })
                  }
                >
                  撤回
                </Button>
                {['REJECTED', 'EXPIRED'].includes(offer.status) && (
                  <Button
                    loading={offerActionLoading === `regenerate-${offer.id}`}
                    onClick={async () => {
                      await runOfferAction(`regenerate-${offer.id}`, '重发Offer', async () => {
                        await apiPost(`/offers/${offer.id}/regenerate`, {
                          overrides: { templateId: offer.templateId }
                        });
                        messageApi.success('已重新生成Offer');
                        await load();
                      });
                    }}
                  >
                    重发Offer
                  </Button>
                )}
                <WorkflowStageTransfer
                  offerId={offer.id}
                  currentStage={offerWorkflowStage(offer)}
                  onTransferred={load}
                />
              </Space>
            )
          }
        ]}
      />
      <Modal
        key={sendOffer?.id || 'closed-offer'}
        title={sendOffer?.status === 'READY_TO_SEND' ? '发送Offer' : 'Offer邮件内容'}
        open={Boolean(sendOffer)}
        width={980}
        onCancel={() => {
          if (!sendingOffer) setSendOffer(null);
        }}
        confirmLoading={sendingOffer}
        okText={sendOffer?.status === 'READY_TO_SEND' ? '确认发送' : '关闭'}
        cancelButtonProps={{ style: sendOffer?.status === 'READY_TO_SEND' ? undefined : { display: 'none' } }}
        onOk={async () => {
          if (!sendOffer) return;
          if (sendOffer.status !== 'READY_TO_SEND') {
            setSendOffer(null);
            return;
          }
          try {
            setSendingOffer(true);
            let offerToSend = sendOffer;
            if (selectedTemplateId && selectedTemplateId !== sendOffer.templateId) {
              offerToSend = await apiPost<Offer>(`/offers/${sendOffer.id}/regenerate`, {
                overrides: { templateId: selectedTemplateId }
              });
            }
            const finalEmailCc = normalizeEmailTags(emailCc);
            const invalidEmailCc = invalidEmailTags(finalEmailCc);
            if (invalidEmailCc.length) {
              messageApi.error(`抄送邮箱格式不正确：${invalidEmailCc.join('、')}`);
              return;
            }
            const sent = await apiPost<Offer>(`/offers/${offerToSend.id}/mark-sent`, {
              letterContent: finalLetterContent,
              emailCc: finalEmailCc
            });
            messageApi.success(
              sent.archiveSummary?.status === 'MOCK'
                ? '模拟发送完成，未发送真实邮件或上传PDF'
                : 'Offer邮件已发送，PDF归档结果请查看列表'
            );
            setSendOffer(null);
            load();
          } catch (error) {
            messageApi.error((error as Error).message || 'Offer邮件发送失败');
          } finally {
            setSendingOffer(false);
          }
        }}
      >
        {sendOffer && (
          <Space direction="vertical" className="full">
            {sendOffer.status === 'READY_TO_SEND' && (
              <Alert
                type="info"
                showIcon
                message="确认发送后，将按本次最终内容生成PDF并归档至钉钉2026offer文件夹。归档失败只重试归档，不重发邮件。"
              />
            )}
            <div>候选人：{sendOffer.candidateName}</div>
            <div>
              收件邮箱：{sendOffer.candidateEmail || '未从录用审批读取到候选人邮箱'}
              {sendOffer.candidateEmailIsVirtual && <Tag color="blue">老审批缺邮箱，已用虚拟邮箱模拟</Tag>}
            </div>
            <div>发件邮箱：{sendOffer.emailFrom || 'shu.song@stardust.ai'}</div>
            <div className="offer-email-cc-field">
              <Typography.Text>
                抄送邮箱{sendOffer.status === 'READY_TO_SEND' ? '（发送前可修改）' : ''}：
              </Typography.Text>
              <Select
                mode="tags"
                tokenSeparators={[',', ';', '，', '；', '\n']}
                value={emailCc}
                disabled={sendOffer.status !== 'READY_TO_SEND'}
                onChange={(values) => setEmailCc(normalizeEmailTags(values))}
                placeholder="输入邮箱后回车，可粘贴多个邮箱"
                className="full"
                options={emailCc.map((email) => ({ value: email, label: email }))}
              />
              {sendOffer.status === 'READY_TO_SEND' && (
                <Typography.Text type="secondary">默认按规则带出；实际发送时以这里显示的抄送邮箱为准。</Typography.Text>
              )}
            </div>
            <div>当前状态：{offerStatusText(sendOffer.status)}</div>
            <div>邮件接收状态：{offerReceiptText(sendOffer)}</div>
            <Select
              value={selectedTemplateId || sendOffer.templateId}
              disabled={sendOffer.status !== 'READY_TO_SEND' || offerActionLoading === `prepare-${sendOffer.id}`}
              options={templates.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => void changeSendTemplate(value)}
              className="full"
            />
            <Tabs
              items={[
                {
                  key: 'letter',
                  label: '录用通知函',
                  children: (
                    <Space direction="vertical" className="full">
                      {editableLetterTemplate ? (
                        <>
                          <Alert
                            type="info"
                            showIcon
                            message="蓝色字段可在发送前核对和修改，固定模板内容需到模板管理中调整。"
                          />
                          <div className="letter-variable-editor">
                            <EditableLetterTemplate
                              template={editableLetterTemplate}
                              values={letterVariables}
                              editable={sendOffer.status === 'READY_TO_SEND'}
                              onChange={(label, value) =>
                                setLetterVariables((current) => ({ ...current, [label]: value }))
                              }
                            />
                          </div>
                          <div className="letter-send-preview">
                            <Typography.Text strong>候选人看到的录用通知函预览</Typography.Text>
                            <pre>{finalLetterContent}</pre>
                          </div>
                        </>
                      ) : (
                        <Form layout="vertical">
                          <Form.Item label="录用通知函正文">
                            <Input.TextArea
                              rows={18}
                              value={letterContent}
                              readOnly={sendOffer.status !== 'READY_TO_SEND'}
                              onChange={(event) => setLetterContent(event.target.value)}
                            />
                          </Form.Item>
                        </Form>
                      )}
                    </Space>
                  )
                },
                {
                  key: 'email',
                  label: '邮件正文',
                  children: (
                    <Form layout="vertical">
                      <Form.Item label="邮件主题">
                        <Input value={currentEmailSubject} readOnly />
                      </Form.Item>
                      <Form.Item label="邮件正文">
                        <Input.TextArea rows={10} value={currentEmailBody} readOnly />
                      </Form.Item>
                    </Form>
                  )
                }
              ]}
            />
            <Space>
              <Button onClick={() => copy(`${location.origin}/offer/${sendOffer.token}`)}>复制链接</Button>
              <Button
                onClick={() =>
                  copy(
                    [
                      '邮件主题：' + currentEmailSubject,
                      '抄送：' + (normalizeEmailTags(emailCc).join('; ') || '无'),
                      '',
                      currentEmailBody
                    ].join('\n')
                  )
                }
              >
                复制邮件
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
    </section>
  );
}

function PublicOfferPage({ token }: { token: string }) {
  const [accessState, setAccessState] = useState<PublicOfferAccessState | null>(null);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [template, setTemplate] = useState<OfferNotificationTemplate | null>(null);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [reason, setReason] = useState('');
  const [messageApi, holder] = message.useMessage();
  const loadAccessState = () =>
    apiGet<PublicOfferAccessState>(`/public/offers/${token}`).then((state) => {
      setAccessState(state);
      return state;
    });
  useEffect(() => {
    setOffer(null);
    setTemplate(null);
    setAccessToken('');
    setReason('');
    setLoading(true);
    void loadAccessState()
      .catch((error) => {
        setAccessState({
          status: 'INVALID',
          requiresVerification: false,
          remainingAttempts: 0,
          maxAttempts: 3,
          locked: true,
          message: humanizeErrorMessage((error as Error).message)
        });
      })
      .finally(() => setLoading(false));
  }, [token]);
  if (loading) return <main className="public-page">加载中...</main>;
  if (!accessState) return <main className="public-page">Offer链接无法识别，请联系HR。</main>;

  const verifyIdentity = async (values: { candidateName?: string; mobile?: string }) => {
    try {
      setVerifying(true);
      const result = await apiPost<PublicOfferVerificationResult>(`/public/offers/${token}/verify`, values);
      setAccessState(result);
      setAccessToken(result.accessToken);
      setOffer(result.offer);
      setTemplate({
        id: 'public-offer-email',
        name: '录用通知邮件',
        subject: result.offer.emailSubject ?? `${result.offer.candidateName}欢迎加入星尘数据`,
        content: result.offer.emailTemplateText ?? ''
      });
    } catch (error) {
      messageApi.error(humanizeErrorMessage((error as Error).message));
      await loadAccessState().catch(() => undefined);
    } finally {
      setVerifying(false);
    }
  };

  if (accessState.requiresVerification && !offer) {
    return (
      <main className="offer-public-page">
        {holder}
        <section className="offer-auth-card">
          <img className="email-logo" src="/logo-stardust.svg" alt="STARDUST" />
          <Typography.Title level={3}>查看录用通知函</Typography.Title>
          <Typography.Paragraph type="secondary">
            请填写候选人姓名和手机号完成身份验证。剩余尝试次数：{accessState.remainingAttempts}/
            {accessState.maxAttempts}
          </Typography.Paragraph>
          <Form layout="vertical" onFinish={verifyIdentity}>
            <Form.Item name="candidateName" label="姓名" rules={[{ required: true, message: '请填写姓名' }]}>
              <Input autoComplete="name" />
            </Form.Item>
            <Form.Item name="mobile" label="手机号" rules={[{ required: true, message: '请填写手机号' }]}>
              <Input autoComplete="tel" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={verifying} block>
              查看Offer
            </Button>
          </Form>
        </section>
      </main>
    );
  }

  if (!accessState.requiresVerification && !offer) {
    return (
      <main className="offer-public-page">
        {holder}
        <section className="offer-auth-card">
          <img className="email-logo" src="/logo-stardust.svg" alt="STARDUST" />
          <Alert type="info" message={accessState.message || statusText(accessState.status)} />
        </section>
      </main>
    );
  }

  if (!offer || !template) return <main className="public-page">加载中...</main>;

  const canAct = offer.status === 'SENT';
  const canContinueOnboarding = offer.status === 'ACCEPTED';
  return (
    <main className="offer-public-page">
      {holder}
      {!canAct && <Alert type="info" message={statusText(offer.status)} />}
      <EmailPreviewCard
        offer={offer}
        template={template}
        subject={fillOfferNotificationText(template.subject, offer)}
        actions={
          canAct ? (
            <Space direction="vertical" className="email-actions">
              <button
                className="candidate-action-button primary"
                onClick={() =>
                  void apiPost(`/public/offers/${token}/accept`, { accessToken })
                    .then(() => {
                      messageApi.success('已接受Offer');
                      window.location.href = `/onboarding/${token}`;
                    })
                    .catch((error) => messageApi.error(humanizeErrorMessage((error as Error).message)))
                }
              >
                接受offer
              </button>
              <Input.TextArea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="拒绝原因"
              />
              <button
                className="candidate-action-button danger"
                onClick={() =>
                  void apiPost<Offer>(`/public/offers/${token}/reject`, { reason, accessToken })
                    .then(setOffer)
                    .catch((error) => messageApi.error(humanizeErrorMessage((error as Error).message)))
                }
              >
                拒绝offer
              </button>
            </Space>
          ) : canContinueOnboarding ? (
            <Space direction="vertical" className="email-actions">
              <button
                className="candidate-action-button primary"
                onClick={() => {
                  window.location.href = `/onboarding/${token}`;
                }}
              >
                继续填写员工档案
              </button>
            </Space>
          ) : null
        }
      />
      <article id="offer-letter" className="letter-preview">
        <Typography.Title level={3}>录用通知函</Typography.Title>
        <pre>{offer.letterContent || '暂无录用通知函内容'}</pre>
      </article>
    </main>
  );
}

function EmailPreviewCard({
  offer,
  template,
  subject,
  actions
}: {
  offer: Offer;
  template: OfferNotificationTemplate;
  subject: string;
  actions?: React.ReactNode;
}) {
  const content = fillOfferNotificationText(template.content, offer);
  return (
    <article className="email-preview-card">
      <header className="email-preview-header">
        <img className="email-logo" src="/logo-stardust.svg" alt="STARDUST" />
      </header>
      <section className="email-preview-body">
        <div className="email-subject">邮件主题：{subject}</div>
        <div className="email-content">{renderOfferLinkText(content)}</div>
        {actions}
      </section>
    </article>
  );
}

function fillOfferNotificationText(text: string, offer: Offer) {
  const deadlineDate = (offer.expiresAt || '').slice(0, 10) || '2026-07-16';
  const issueDate = offer.emailSentAt?.slice(0, 10) || new Date().toISOString().slice(0, 10);
  return text
    .replaceAll('【候选人姓名】', offer.candidateName)
    .replaceAll('【姓名】', offer.candidateName)
    .replaceAll('【岗位名称】', offer.position)
    .replaceAll('【职位】', offer.position)
    .replaceAll('【岗位名称】', offer.position)
    .replaceAll('【部门】', displayDepartment(offer.departmentName))
    .replaceAll('【入职日期】', offer.expectedOnboardDate)
    .replaceAll('【预计入职日期】', offer.expectedOnboardDate)
    .replaceAll('【日期（发offer3天内）】', deadlineDate)
    .replaceAll('【时间】', deadlineDate)
    .replaceAll('【发offer日期】', issueDate)
    .replaceAll('【发放offer日期】', issueDate);
}

function renderTableText(value: unknown) {
  return <span className="table-cell-wrap">{String(value ?? '-')}</span>;
}

function renderNameText(value: unknown) {
  return <span className="table-cell-wrap table-cell-nowrap">{String(value ?? '-')}</span>;
}

function toStringRecord(value: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, String(item ?? '')]));
}

function offerEmailCcValue(offer: Offer) {
  return normalizeEmailTags(offer.emailCc ?? offer.emailCcPreview ?? []);
}

export function normalizeEmailTags(values: string[]) {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const value of values) {
    const parts = String(value ?? '')
      .split(/[;；,，\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
    for (const email of parts) {
      const normalized = email.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      emails.push(email);
    }
  }
  return emails;
}

export function invalidEmailTags(values: string[]) {
  return normalizeEmailTags(values).filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

export function offerArchiveStatusText(status?: string) {
  const labels: Record<string, string> = {
    SENDING: '邮件发送结果待确认',
    MAIL_FAILED: '邮件失败，未归档',
    PENDING: '待归档',
    UPLOADING: '归档核验中',
    SUCCEEDED: '已归档',
    FAILED: '归档失败',
    UNKNOWN: '归档结果待核验',
    MOCK: '模拟发送，未上传',
    LEGACY: '历史发送，未自动归档'
  };
  return labels[status ?? 'LEGACY'] ?? '归档状态待确认';
}

export function offerReceiptText(offer: Offer) {
  if (offer.status === 'READY_TO_SEND') return '待发送';
  if (offer.status === 'SENT' && offer.candidateReadAt) return '已查看';
  if (offer.status === 'SENT') return '待查看';
  if (offer.status === 'ACCEPTED') return '已接受';
  if (offer.status === 'CANCELLED' && offer.rejectedAt) return '已拒绝';
  if (offer.status === 'CANCELLED') return '已取消';
  if (offer.status === 'EXPIRED') return '已失效';
  if (offer.status === 'WITHDRAWN') return '已撤回';
  return offerStatusText(offer.status);
}

function withGenderSalutation(values: Record<string, string>, gender?: string) {
  const existing = values['称谓'] || values['性别称谓'];
  const salutation =
    !existing || existing === '男士/女士' || existing === '先生/女士'
      ? genderSalutation(gender)
      : existing === '男士'
        ? '先生'
        : existing;
  return { ...values, 称谓: salutation, 性别称谓: salutation };
}

export function genderSalutation(gender?: string) {
  const value = String(gender ?? '')
    .trim()
    .toLowerCase();
  if (!value) return '先生/女士';
  if (['女', '女性', 'female', 'f', '女士'].some((item) => value.includes(item))) return '女士';
  if (['男', '男性', 'male', 'm', '先生', '男士'].some((item) => value.includes(item))) return '先生';
  return '先生/女士';
}

export function humanizeErrorMessage(message: string) {
  const fieldLabels: Record<string, string> = {
    declaration_confirmed: '本人声明',
    id_name: '身份证姓名',
    id_number: '证件号码',
    birth_date: '出生日期',
    current_address: '住址',
    gender: '性别',
    ethnicity: '民族',
    id_address: '身份证地址',
    household_type: '户籍类型',
    marital_status: '婚姻状况',
    first_work_date: '首次参加工作时间',
    bank_card_number: '银行卡号',
    bank_name: '开户行',
    id_card_front: '身份证（人像面）',
    id_card_back: '身份证（国徽面）',
    education_certificate: '学历证书',
    degree_certificate: '学位证书',
    resignation_certificate: '前公司离职证明',
    employee_photo: '员工照片'
  };
  let readable = message || '提交失败，请检查信息后重试';
  for (const [field, label] of Object.entries(fieldLabels)) {
    readable = readable.replaceAll(`'${field}' is required`, `请填写${label}`).replaceAll(field, label);
  }
  return readable.replaceAll('is required', '不能为空');
}

export function formValuesToMaterialPayload(values: Record<string, unknown>, attachments: Record<string, unknown>[]) {
  const educations = cleanHistoryEntries(values.educations, 4);
  const workExperiences = cleanHistoryEntries(values.workExperiences, 3);
  const {
    educations: _educations,
    workExperiences: _workExperiences,
    contact_name: _contactName,
    contact_relationship: _contactRelationship,
    contact_phone: _contactPhone,
    has_children: _hasChildren,
    child_name: _childName,
    child_gender: _childGender,
    child_birth_date: _childBirthDate,
    ...formData
  } = values;
  return {
    formData: { ...formData, declaration_confirmed: values.declaration_confirmed },
    educations,
    workExperiences,
    emergencyContacts: [
      { name: values.contact_name, relationship: values.contact_relationship, phone: values.contact_phone }
    ].filter((item) => Object.values(item).some(Boolean)),
    familyMembers: [
      {
        has_children: values.has_children,
        child_name: values.has_children === '有' ? values.child_name : undefined,
        child_gender: values.has_children === '有' ? values.child_gender : undefined,
        child_birth_date: values.has_children === '有' ? values.child_birth_date : undefined
      }
    ].filter((item) => Object.values(item).some(Boolean)),
    attachments: sanitizeMaterialAttachments(attachments)
  };
}

function cleanHistoryEntries(value: unknown, limit: number) {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, limit)
    .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}))
    .filter((entry) => Object.values(entry).some((field) => String(field ?? '').trim()));
}

const safeAttachmentFields = [
  'attachmentType',
  'fileName',
  'fileSize',
  'mimeType',
  'fileKey',
  'storageProvider',
  'uploadStatus',
  'dingtalkFileId',
  'mediaId',
  'uploadedAt'
];

export function sanitizeMaterialAttachments(attachments: Record<string, unknown>[]) {
  return attachments.map((attachment) =>
    Object.fromEntries(
      safeAttachmentFields.flatMap((field) => {
        const value = attachment[field];
        return value === undefined ? [] : [[field, value]];
      })
    )
  );
}

export function materialToFormValues(material: Material) {
  const formData = material.formData ?? {};
  const contact = material.emergencyContacts?.[0] ?? {};
  const family = material.familyMembers?.[0] ?? {};
  const educations = material.educations?.length
    ? material.educations
    : [
        {
          education_level: formData.education_level,
          school_name: formData.school_name,
          graduation_date: formData.graduation_date,
          major: formData.major
        }
      ];
  return {
    ...formData,
    educations,
    workExperiences: material.workExperiences ?? [],
    contact_name: contact.name,
    contact_relationship: contact.relationship,
    contact_phone: contact.phone,
    has_children: yesNoFormValue(family.has_children),
    child_name: family.child_name,
    child_gender: family.child_gender,
    child_birth_date: family.child_birth_date
  };
}

function yesNoFormValue(value: unknown) {
  if (value === true) return '有';
  if (value === false) return '无';
  return value;
}

export function onboardingTabForField(fieldName: string | (string | number)[]) {
  const rootName = Array.isArray(fieldName) ? String(fieldName[0] ?? '') : fieldName;
  const archiveFields = new Set([
    'educations',
    'workExperiences',
    'bank_card_number',
    'bank_name',
    'contact_name',
    'contact_relationship',
    'contact_phone',
    'has_children',
    'child_name',
    'child_gender',
    'child_birth_date'
  ]);
  return archiveFields.has(rootName) ? 'archive' : 'basic';
}

const ethnicityOptions = [
  '汉族',
  '阿昌族',
  '白族',
  '保安族',
  '布朗族',
  '布依族',
  '朝鲜族',
  '傣族',
  '达斡尔族',
  '德昂族',
  '侗族',
  '东乡族',
  '独龙族',
  '鄂伦春族',
  '俄罗斯族',
  '鄂温克族',
  '高山族',
  '仡佬族',
  '哈尼族',
  '哈萨克族',
  '赫哲族',
  '回族',
  '景颇族',
  '京族',
  '基诺族',
  '柯尔克孜族',
  '拉祜族',
  '黎族',
  '傈僳族',
  '珞巴族',
  '满族',
  '毛南族',
  '蒙古族',
  '门巴族',
  '苗族',
  '仫佬族',
  '纳西族',
  '怒族',
  '普米族',
  '羌族',
  '撒拉族',
  '畲族',
  '水族',
  '塔吉克族',
  '塔塔尔族',
  '土家族',
  '土族',
  '佤族',
  '维吾尔族',
  '乌孜别克族',
  '锡伯族',
  '瑶族',
  '彝族',
  '裕固族',
  '藏族',
  '壮族'
];
const householdOptions = [
  '本地城镇',
  '本地农村',
  '外地城镇（省内）',
  '外地农村（省内）',
  '外地城镇（省外）',
  '外地农村（省外）'
];
const maritalOptions = ['未婚', '已婚', '离异', '丧偶', '分居'];
const educationOptions = ['初中', '高中', '中专', '大专', '本科', '硕士', '博士', '其他'];
const relationshipOptions = ['父母', '配偶', '子女', '其他'];
const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const monthPattern = /^\d{4}-\d{2}$/;

export const ONBOARDING_TRUTHFULNESS_NOTICE =
  '请确认所填写及上传的资料真实、准确、完整。提供虚假、隐瞒或误导性信息可能影响录用、劳动合同履行及公司依法采取的后续处理。';

function selectOptions(values: string[]) {
  return values.map((value) => ({ value, label: value }));
}

function requiredDateRules(label: string) {
  return [
    { required: true, message: `请填写${label}` },
    { pattern: datePattern, message: `${label}必须为YYYY-MM-DD` }
  ];
}

function requiredMonthRules(label: string) {
  return [
    { required: true, message: `请填写${label}` },
    { pattern: monthPattern, message: `${label}必须为YYYY-MM` }
  ];
}

function optionalDateOrLongRules(label: string) {
  return [
    {
      validator: (_: unknown, value: unknown) => {
        const text = String(value ?? '').trim();
        if (!text || text === '长期' || datePattern.test(text)) return Promise.resolve();
        return Promise.reject(new Error(`${label}必须为YYYY-MM-DD或长期`));
      }
    }
  ];
}

function fillLetterTemplate(template: string, values: Record<string, string>) {
  const salutation = values['称谓'] || values['性别称谓'] || '先生/女士';
  return template
    .replaceAll('男士/女士', salutation)
    .replaceAll('先生/女士', salutation)
    .replace(/【([^】]+)】/g, (_, label: string) => values[label] ?? '');
}

function EditableLetterTemplate({
  template,
  values,
  editable,
  onChange
}: {
  template: string;
  values: Record<string, string>;
  editable: boolean;
  onChange: (label: string, value: string) => void;
}) {
  const parts = template.split(/(【[^】]+】)/g).filter((part) => part !== '');
  return (
    <div className="letter-template-flow">
      {parts.map((part, index) => {
        const match = part.match(/^【([^】]+)】$/);
        if (!match) {
          const salutation = values['称谓'] || values['性别称谓'] || '先生/女士';
          const fixedText = part.replaceAll('男士/女士', salutation).replaceAll('先生/女士', salutation);
          return (
            <span className="letter-fixed-text" key={`${part}-${index}`}>
              {fixedText}
            </span>
          );
        }
        const label = match[1];
        return (
          <span className="letter-variable-field" key={`${label}-${index}`}>
            <span className="letter-variable-label">{label}</span>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={values[label] ?? ''}
              readOnly={!editable}
              onChange={(event) => onChange(label, event.target.value)}
            />
          </span>
        );
      })}
    </div>
  );
}

function attachmentFields() {
  return [
    { type: 'id_card_front', label: '身份证（人像面）' },
    { type: 'id_card_back', label: '身份证（国徽面）' },
    { type: 'education_certificate', label: '学历证书' },
    { type: 'degree_certificate', label: '学位证书' },
    { type: 'resignation_certificate', label: '前公司离职证明' },
    { type: 'employee_photo', label: '员工照片' }
  ];
}

function OnboardingFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="onboarding-form-section">
      <Typography.Title level={4}>{title}</Typography.Title>
      <div className="onboarding-form-grid">{children}</div>
    </section>
  );
}

function AttachmentUploadField({
  token,
  type,
  label,
  uploaded,
  onUploaded
}: {
  token: string;
  type: string;
  label: string;
  uploaded?: Record<string, unknown>;
  onUploaded: (attachment: Record<string, unknown>) => void;
}) {
  const [messageApi, holder] = message.useMessage();
  return (
    <div className="attachment-upload-row">
      {holder}
      <div className="attachment-upload-meta">
        <Typography.Text strong>{label}</Typography.Text>
        <Typography.Text type="secondary">支持 JPEG、JPG、PNG、PDF，单个文件 50MB 内</Typography.Text>
      </div>
      <div className="attachment-upload-action">
        <Upload
          showUploadList={false}
          beforeUpload={async (file) => {
            try {
              const contentBase64 = await readFileBase64(file);
              const attachment = await apiPost<Record<string, unknown>>(`/public/onboarding/${token}/attachments`, {
                attachmentType: type,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
                contentBase64
              });
              onUploaded(attachment);
              messageApi.success('已上传');
            } catch (error) {
              messageApi.error((error as Error).message);
            }
            return Upload.LIST_IGNORE;
          }}
        >
          <Button>上传</Button>
        </Upload>
        <Typography.Text className="attachment-upload-status" type={uploaded ? undefined : 'secondary'}>
          {uploaded ? String(uploaded.fileName ?? '已上传') : '未上传'}
        </Typography.Text>
      </div>
    </div>
  );
}

function readFileBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.onload = () => {
      const value = String(reader.result ?? '');
      resolve(value.includes(',') ? value.split(',')[1] : value);
    };
    reader.readAsDataURL(file);
  });
}

function renderOfferLinkText(text: string) {
  return text.split('\n').map((line, index) => {
    const parts = line.split('《录用通知函》');
    const renderedLine =
      parts.length === 1 ? (
        line
      ) : (
        <>
          {parts[0]}
          <a href="#offer-letter">《录用通知函》</a>
          {parts.slice(1).join('《录用通知函》')}
        </>
      );
    return <p key={`${line}-${index}`}>{renderedLine || '\u00a0'}</p>;
  });
}

function PublicOnboardingPage({ token }: { token: string }) {
  const [data, setData] = useState<{ offer: Offer; material: Material } | null>(null);
  const [attachments, setAttachments] = useState<Record<string, unknown>[]>([]);
  const [draftStatus, setDraftStatus] = useState('');
  const [activeOnboardingTab, setActiveOnboardingTab] = useState('basic');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const hasChildren = Form.useWatch('has_children', form);
  const draftTimerRef = useRef<number | undefined>(undefined);
  const [messageApi, holder] = message.useMessage();
  useEffect(() => {
    void apiGet<{ offer: Offer; material: Material }>(`/public/onboarding/${token}`).then((result) => {
      setData(result);
      setAttachments(result.material.attachments ?? []);
      form.setFieldsValue(materialToFormValues(result.material));
    });
  }, [form, token]);
  if (!data) return <main className="public-page">加载中...</main>;

  const saveDraft = async (values: Record<string, unknown>, currentAttachments = attachments, showMessage = false) => {
    if (!data || ['SUBMITTED', 'APPROVED', 'SYNCED'].includes(data.material.status)) return;
    try {
      await apiPost(`/public/onboarding/${token}/draft`, formValuesToMaterialPayload(values, currentAttachments));
      setDraftStatus(`已自动保存 ${new Date().toLocaleTimeString('zh-CN', { hour12: false })}`);
      if (showMessage) messageApi.success('已存入草稿箱，下次打开可继续填写');
    } catch {
      setDraftStatus('自动保存失败，请检查网络后继续填写');
      if (showMessage) messageApi.error('存入草稿箱失败，请检查网络后重试');
    }
  };

  const submit = async (values: Record<string, unknown>) => {
    try {
      setSubmitting(true);
      await apiPost(`/public/onboarding/${token}/submit`, formValuesToMaterialPayload(values, attachments));
      messageApi.success('入职资料已提交');
    } catch (error) {
      messageApi.error(humanizeErrorMessage((error as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFailed = (errorInfo: { errorFields?: { name: (string | number)[]; errors: string[] }[] }) => {
    const firstError = errorInfo.errorFields?.[0];
    if (firstError?.name?.length) setActiveOnboardingTab(onboardingTabForField(firstError.name));
    const errorMessages = (errorInfo.errorFields ?? [])
      .flatMap((item) => item.errors)
      .filter(Boolean)
      .slice(0, 3);
    messageApi.error(errorMessages.length ? `请完善：${errorMessages.join('、')}` : '请完善必填信息后再提交');
  };

  const scheduleDraftSave = (values = form.getFieldsValue(true), currentAttachments = attachments) => {
    if (draftTimerRef.current) window.clearTimeout(draftTimerRef.current);
    draftTimerRef.current = window.setTimeout(() => void saveDraft(values, currentAttachments), 800);
  };

  const renderAttachment = (type: string) => {
    const field = attachmentFields().find((item) => item.type === type);
    if (!field) return null;
    return (
      <AttachmentUploadField
        key={field.type}
        token={token}
        type={field.type}
        label={field.label}
        uploaded={attachments.find((item) => item.attachmentType === field.type)}
        onUploaded={(attachment) =>
          setAttachments((current) => {
            const next = [...current.filter((item) => item.attachmentType !== field.type), attachment];
            scheduleDraftSave(form.getFieldsValue(true), next);
            return next;
          })
        }
      />
    );
  };

  return (
    <main className="public-page wide onboarding-public-page">
      {holder}
      <header className="onboarding-form-header">
        <img className="onboarding-form-logo" src="/logo-stardust.svg" alt="STARDUST" />
        <div>
          <Typography.Title>入职登记表</Typography.Title>
          <Typography.Text type="secondary">{draftStatus || '填写过程中会自动保存草稿'}</Typography.Text>
        </div>
      </header>
      <Alert
        className="onboarding-form-summary"
        message={`${data.offer.candidateName} / ${data.offer.departmentName} / ${data.offer.position}`}
      />
      <Alert
        className="onboarding-truthfulness-alert"
        type="warning"
        showIcon
        message="资料真实性确认"
        description={ONBOARDING_TRUTHFULNESS_NOTICE}
      />
      <Form
        className="onboarding-form"
        form={form}
        layout="vertical"
        onFinish={submit}
        onFinishFailed={handleSubmitFailed}
        onValuesChange={(_, values) => scheduleDraftSave(values, attachments)}
      >
        <Tabs
          activeKey={activeOnboardingTab}
          onChange={setActiveOnboardingTab}
          items={[
            {
              key: 'basic',
              label: '基础信息',
              children: (
                <>
                  <OnboardingFormSection title="身份信息">
                    <Form.Item
                      name="id_name"
                      label="身份证姓名"
                      rules={[{ required: true, message: '请填写身份证姓名' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="id_number"
                      label="证件号码"
                      rules={[{ required: true, message: '请填写证件号码' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="birth_date" label="出生日期" rules={requiredDateRules('出生日期')}>
                      <Input placeholder="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item name="gender" label="性别" rules={[{ required: true, message: '请填写性别' }]}>
                      <Select options={[{ value: '男' }, { value: '女' }]} />
                    </Form.Item>
                    <Form.Item name="ethnicity" label="民族" rules={[{ required: true, message: '请选择民族' }]}>
                      <Select showSearch options={selectOptions(ethnicityOptions)} />
                    </Form.Item>
                    <Form.Item name="id_valid_until" label="证件有效期" rules={optionalDateOrLongRules('证件有效期')}>
                      <Input placeholder="YYYY-MM-DD 或 长期" />
                    </Form.Item>
                    <Form.Item
                      className="onboarding-form-span"
                      name="id_address"
                      label="身份证地址"
                      rules={[{ required: true, message: '请填写身份证地址' }]}
                    >
                      <Input />
                    </Form.Item>
                    <div className="onboarding-attachment-grid onboarding-form-span">
                      {renderAttachment('id_card_front')}
                      {renderAttachment('id_card_back')}
                    </div>
                  </OnboardingFormSection>
                  <OnboardingFormSection title="户籍与个人状态">
                    <Form.Item
                      name="household_type"
                      label="户籍类型"
                      rules={[{ required: true, message: '请填写户籍类型' }]}
                    >
                      <Select options={selectOptions(householdOptions)} />
                    </Form.Item>
                    <Form.Item name="current_address" label="住址" rules={[{ required: true, message: '请填写住址' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="political_status" label="政治面貌">
                      <Select options={[{ value: '群众' }, { value: '党员' }, { value: '团员' }, { value: '其他' }]} />
                    </Form.Item>
                    <Form.Item
                      name="marital_status"
                      label="婚姻状况"
                      rules={[{ required: true, message: '请填写婚姻状况' }]}
                    >
                      <Select options={selectOptions(maritalOptions)} />
                    </Form.Item>
                    <Form.Item
                      name="first_work_date"
                      label="首次参加工作时间"
                      rules={requiredDateRules('首次参加工作时间')}
                    >
                      <Input placeholder="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item name="social_security_account" label="个人社保账号">
                      <Input />
                    </Form.Item>
                    <Form.Item name="housing_fund_account" label="个人公积金账号">
                      <Input />
                    </Form.Item>
                    <div className="onboarding-attachment-grid onboarding-form-span">
                      {renderAttachment('employee_photo')}
                    </div>
                  </OnboardingFormSection>
                </>
              )
            },
            {
              key: 'archive',
              label: '档案信息',
              children: (
                <>
                  <OnboardingFormSection title="学历信息">
                    <div className="onboarding-form-span">
                      <Typography.Paragraph type="secondary">
                        请按最近到最早的顺序填写，最多 4 条。
                      </Typography.Paragraph>
                      <Form.List name="educations">
                        {(fields, { add, remove }) => (
                          <div className="onboarding-history-list">
                            {fields.map((field, index) => (
                              <section className="onboarding-history-row" key={field.key}>
                                <div className="onboarding-history-heading">
                                  <Typography.Text strong>学历经历 {index + 1}</Typography.Text>
                                  {fields.length > 1 && <Button onClick={() => remove(field.name)}>删除</Button>}
                                </div>
                                <div className="onboarding-form-grid">
                                  <Form.Item
                                    name={[field.name, 'education_level']}
                                    label="学历"
                                    rules={[{ required: true, message: '请填写学历' }]}
                                  >
                                    <Select options={selectOptions(educationOptions)} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'school_name']}
                                    label="学校"
                                    rules={[{ required: true, message: '请填写学校' }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'start_month']}
                                    label="开始年月"
                                    rules={requiredMonthRules('开始年月')}
                                  >
                                    <Input placeholder="YYYY-MM" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'end_month']}
                                    label="结束年月"
                                    rules={requiredMonthRules('结束年月')}
                                  >
                                    <Input placeholder="YYYY-MM" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'graduation_date']}
                                    label="毕业时间"
                                    rules={requiredDateRules('毕业时间')}
                                  >
                                    <Input placeholder="YYYY-MM-DD" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'major']}
                                    label="专业"
                                    rules={[{ required: true, message: '请填写专业' }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'completion_status']}
                                    label="学业状态"
                                    rules={[{ required: true, message: '请选择学业状态' }]}
                                  >
                                    <Select options={selectOptions(['毕业', '结业', '肄业'])} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'degree']}
                                    label="学位"
                                    rules={[{ required: true, message: '请填写学位' }]}
                                  >
                                    <Input placeholder="例如：学士、硕士" />
                                  </Form.Item>
                                </div>
                              </section>
                            ))}
                            <Button disabled={fields.length >= 4} onClick={() => add()}>
                              添加学历经历
                            </Button>
                          </div>
                        )}
                      </Form.List>
                    </div>
                    <div className="onboarding-attachment-grid onboarding-form-span">
                      {renderAttachment('education_certificate')}
                      {renderAttachment('degree_certificate')}
                    </div>
                  </OnboardingFormSection>
                  <OnboardingFormSection title="工作经历">
                    <div className="onboarding-form-span">
                      <Typography.Paragraph type="secondary">
                        请按最近到最早的顺序填写，最多 3 条；无工作经历可保持空白。
                      </Typography.Paragraph>
                      <Form.List name="workExperiences">
                        {(fields, { add, remove }) => (
                          <div className="onboarding-history-list">
                            {fields.map((field, index) => (
                              <section className="onboarding-history-row" key={field.key}>
                                <div className="onboarding-history-heading">
                                  <Typography.Text strong>工作经历 {index + 1}</Typography.Text>
                                  <Button onClick={() => remove(field.name)}>删除</Button>
                                </div>
                                <div className="onboarding-form-grid">
                                  <Form.Item
                                    name={[field.name, 'start_month']}
                                    label="开始年月"
                                    rules={requiredMonthRules('开始年月')}
                                  >
                                    <Input placeholder="YYYY-MM" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'end_month']}
                                    label="结束年月"
                                    rules={requiredMonthRules('结束年月')}
                                  >
                                    <Input placeholder="YYYY-MM" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'employer']}
                                    label="工作单位"
                                    rules={[{ required: true, message: '请填写工作单位' }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'position']}
                                    label="职务"
                                    rules={[{ required: true, message: '请填写职务' }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[field.name, 'leaving_reason']} label="离职原因">
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[field.name, 'reference_name']} label="证明人">
                                    <Input />
                                  </Form.Item>
                                </div>
                              </section>
                            ))}
                            <Button disabled={fields.length >= 3} onClick={() => add()}>
                              添加工作经历
                            </Button>
                          </div>
                        )}
                      </Form.List>
                    </div>
                  </OnboardingFormSection>
                  <OnboardingFormSection title="工资卡信息">
                    <Form.Item
                      name="bank_card_number"
                      label="银行卡号"
                      rules={[{ required: true, message: '请填写银行卡号' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="bank_name"
                      label="开户行"
                      extra="工资卡开户行必须为招商银行。"
                      rules={[
                        {
                          validator: (_, value) => {
                            const text = String(value ?? '').trim();
                            if (!text) return Promise.reject(new Error('请填写开户行'));
                            if (!text.includes('招商')) return Promise.reject(new Error('工资卡开户行必须为招商银行'));
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input placeholder="请填写招商银行开户行，例如：招商银行北京分行营业部" />
                    </Form.Item>
                  </OnboardingFormSection>
                  <OnboardingFormSection title="紧急联系人">
                    <Form.Item
                      name="contact_name"
                      label="紧急联系人"
                      rules={[{ required: true, message: '请填写紧急联系人' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="contact_relationship"
                      label="关系"
                      rules={[{ required: true, message: '请填写联系人关系' }]}
                    >
                      <Select options={selectOptions(relationshipOptions)} />
                    </Form.Item>
                    <Form.Item
                      name="contact_phone"
                      label="联系电话"
                      rules={[{ required: true, message: '请填写联系人电话' }]}
                    >
                      <Input />
                    </Form.Item>
                  </OnboardingFormSection>
                  <OnboardingFormSection title="家庭与离职材料">
                    <Form.Item
                      name="has_children"
                      label="有无子女"
                      rules={[{ required: true, message: '请选择有无子女' }]}
                    >
                      <Select
                        options={[{ value: '无' }, { value: '有' }]}
                        onChange={(value) => {
                          if (value === '无') {
                            form.setFieldsValue({
                              child_name: undefined,
                              child_gender: undefined,
                              child_birth_date: undefined
                            });
                          }
                        }}
                      />
                    </Form.Item>
                    {hasChildren === '有' && (
                      <>
                        <Form.Item
                          name="child_name"
                          label="子女姓名"
                          rules={[{ required: true, message: '请填写子女姓名' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="child_gender"
                          label="子女性别"
                          rules={[{ required: true, message: '请选择子女性别' }]}
                        >
                          <Select options={[{ value: '男' }, { value: '女' }]} />
                        </Form.Item>
                        <Form.Item
                          name="child_birth_date"
                          label="子女出生日期"
                          rules={requiredDateRules('子女出生日期')}
                        >
                          <Input placeholder="YYYY-MM-DD" />
                        </Form.Item>
                      </>
                    )}
                    <div className="onboarding-attachment-grid onboarding-form-span">
                      {renderAttachment('resignation_certificate')}
                    </div>
                  </OnboardingFormSection>
                </>
              )
            }
          ]}
        />
        <Form.Item
          name="declaration_confirmed"
          valuePropName="checked"
          rules={[
            { validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error('请勾选本人声明'))) }
          ]}
        >
          <Checkbox>{ONBOARDING_TRUTHFULNESS_NOTICE}</Checkbox>
        </Form.Item>
        <Space>
          <Button onClick={() => void saveDraft(form.getFieldsValue(true), attachments, true)}>存入草稿箱</Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            提交
          </Button>
        </Space>
      </Form>
    </main>
  );
}

export function MaterialReview() {
  const { items, load, error: listError } = useLocalList<Material>('/hr/onboarding');
  const [detail, setDetail] = useAuthorizedSelection<Material>(items);
  const [messageApi, holder] = message.useMessage();
  return (
    <section>
      {holder}
      <PageTitle title="员工档案" />
      <FilteredTable
        config={{ ...personnelConfig('pending-onboarding'), key: 'material-review' }}
        error={listError}
        project={projectMaterialList}
        className="data-table"
        rowKey="id"
        dataSource={items}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', width: 120, render: (value) => renderTableText(value || '-') },
          { title: '手机号', dataIndex: 'mobile', width: 140, render: (value) => renderTableText(value || '-') },
          {
            title: '员工类型',
            dataIndex: 'employeeType',
            width: 110,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value || '-'))
          },
          { title: '职位', dataIndex: 'position', width: 220, render: (value) => renderTableText(value || '-') },
          {
            title: '预计入职日期',
            dataIndex: 'expectedOnboardDate',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: 'Offer状态',
            dataIndex: 'offerStatus',
            width: 120,
            render: (status) => <Tag>{offerStatusText(status || '-')}</Tag>
          },
          {
            title: '档案状态',
            dataIndex: 'status',
            width: 130,
            render: (status) => <Tag>{materialStatusText(status)}</Tag>
          },
          {
            title: '智能人事同步',
            width: 260,
            render: (_, item) => (
              <Space direction="vertical" size={0}>
                {hrmSyncDisplay(item)}
                {dingTalkAccountDisplay(item)}
                {employeeEmailDisplay(item)}
                {item.errorMessage && <Typography.Text type="danger">{item.errorMessage}</Typography.Text>}
              </Space>
            )
          },
          {
            title: '提醒记录',
            width: 180,
            render: (_, item) => (
              <Space direction="vertical" size={0}>
                <span>{item.reminderCount ? `已提醒 ${item.reminderCount} 次` : '未提醒'}</span>
                {item.lastRemindedAt && <Typography.Text type="secondary">{item.lastRemindedAt}</Typography.Text>}
              </Space>
            )
          },
          {
            title: '操作',
            width: 260,
            render: (_, item) => (
              <Space wrap>
                <Button
                  onClick={async () => {
                    const data = await apiGet<Material>(`/hr/onboarding/${item.id}`);
                    setDetail(data);
                  }}
                >
                  查看
                </Button>
                {item.status === 'PENDING_SUBMIT' && (
                  <Button
                    onClick={async () => {
                      await apiPost(`/hr/onboarding/${item.id}/remind`);
                      messageApi.success('已记录再次提醒，等待后续接入真实通知');
                      load();
                    }}
                  >
                    再次提醒
                  </Button>
                )}
              </Space>
            )
          }
        ]}
      />
      <MaterialDetailModal detail={detail} onClose={() => setDetail(null)} />
    </section>
  );
}

type OnboardingAction = 'entry' | 'account' | 'email' | 'material';

async function provisionOnboardingAccount(item: Material) {
  const endpoint =
    isInvitationPending(item) && !hasJoinedDingTalkCompany(item) ? 'sync-live-status' : 'provision-account';
  const payload = endpoint === 'sync-live-status' ? { force: true } : undefined;
  const response = await apiPost<{
    result?: Record<string, unknown> | { account?: Record<string, unknown> };
  }>(`/hr/onboarding/${item.id}/${endpoint}`, payload);
  const accountResult =
    endpoint === 'sync-live-status' && response.result && 'account' in response.result
      ? (response.result.account as Record<string, unknown> | undefined)
      : (response.result as Record<string, unknown> | undefined);
  return readActionMessage(accountResult, '钉钉账号状态已刷新');
}

async function provisionOnboardingEmail(item: Material) {
  return apiPost<MailboxCreationResponse>(`/hr/onboarding/${item.id}/provision-email`);
}

function OnboardingManagement({ canViewMaterialDetail }: { canViewMaterialDetail: boolean }) {
  const [emailCreation, setEmailCreation] = useState<MailboxCreationResponse>();
  const { items, load, error: listError } = useLocalList<Material>('/hr/onboarding');
  const [active, setActive] = useAuthorizedSelection<Material>(items);
  const [action, setAction] = useState<OnboardingAction>('account');
  const [actionLoading, setActionLoading] = useState('');
  const refreshingRef = useRef(false);
  const [messageApi, holder] = message.useMessage();
  const reloadActive = async (id: string) => {
    const data = await apiGet<Material>(`/hr/onboarding/${id}`);
    setActive(data);
    await load();
  };
  const refreshFromServer = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      await load();
    } finally {
      refreshingRef.current = false;
    }
  };
  useEffect(() => {
    const timer = window.setInterval(() => void refreshFromServer(), DASHBOARD_REFRESH_INTERVAL_MS);
    const refreshOnFocus = () => void refreshFromServer();
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, []);
  const rows = items.filter((item) => isPendingOnboardingItem(item));
  const openAction = async (item: Material, nextAction: OnboardingAction) => {
    const data = await apiGet<Material>(`/hr/onboarding/${item.id}`);
    setAction(nextAction);
    setActive(data);
  };
  const runMaterialAction = async (loadingKey: string, actionName: string, task: () => Promise<void>) => {
    try {
      setActionLoading(loadingKey);
      await task();
    } catch (error) {
      messageApi.error(`${actionName}失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setActionLoading('');
    }
  };
  return (
    <section>
      {holder}
      <PageTitle title="待入职人员" />
      <MailboxCreationDialog response={emailCreation} onClose={() => setEmailCreation(undefined)} />
      <FilteredTable
        config={personnelConfig('pending-onboarding')}
        error={listError}
        project={projectMaterialList}
        className="data-table"
        rowKey="id"
        dataSource={rows}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', width: 120, render: (value) => renderTableText(value || '-') },
          { title: '手机号', dataIndex: 'mobile', width: 140, render: (value) => renderTableText(value || '-') },
          {
            title: '员工类型',
            dataIndex: 'employeeType',
            width: 110,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value || '-'))
          },
          { title: '职位', dataIndex: 'position', width: 220, render: (value) => renderTableText(value || '-') },
          {
            title: '预计入职日期',
            dataIndex: 'expectedOnboardDate',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 130,
            render: (status) => <Tag>{materialStatusText(status)}</Tag>
          },
          { title: '钉钉账号', width: 190, render: (_, item) => dingTalkAccountDisplay(item) },
          { title: '员工邮箱', width: 220, render: (_, item) => employeeEmailDisplay(item) },
          { title: '智能人事档案', width: 220, render: (_, item) => hrmSyncDisplay(item) },
          {
            title: '操作',
            width: 360,
            render: (_, item) => {
              const canConfirmArrival = ['PENDING_SUBMIT', 'SUBMITTED', 'SYNC_FAILED'].includes(item.status);
              const canUseEntry = canUseOnboardingCompletionEntry(item);
              if (!item.arrivedAt) {
                return (
                  <Space wrap>
                    {canUseEntry && <Button onClick={() => openAction(item, 'entry')}>入职登记入口</Button>}
                    {canViewMaterialDetail && !item.id.startsWith('offer-') && (
                      <Button onClick={() => openAction(item, 'material')}>查看档案</Button>
                    )}
                    <Button
                      type="primary"
                      disabled={!canConfirmArrival}
                      loading={actionLoading === `arrival-${item.id}`}
                      onClick={async () => {
                        await runMaterialAction(`arrival-${item.id}`, '确认到岗', async () => {
                          await apiPost(`/hr/onboarding/${item.id}/confirm-arrival`);
                          messageApi.success('已确认到岗，可以发出钉钉邀请');
                          await load();
                        });
                      }}
                    >
                      确认到岗
                    </Button>
                    <WorkflowStageTransfer
                      offerId={item.offerId}
                      currentStage={materialWorkflowStage(item)}
                      onTransferred={load}
                    />
                  </Space>
                );
              }
              const joinedCompany = hasJoinedDingTalkCompany(item);
              return (
                <Space wrap>
                  {canUseEntry && <Button onClick={() => openAction(item, 'entry')}>入职登记入口</Button>}
                  <Button disabled={joinedCompany} onClick={() => openAction(item, 'account')}>
                    {accountActionText(item)}
                  </Button>
                  {canViewMaterialDetail && <Button onClick={() => openAction(item, 'material')}>查看档案</Button>}
                  <Button
                    onClick={async () => {
                      await runMaterialAction(`reverse-${item.id}`, '反向同步档案', async () => {
                        await apiPost(`/hr/onboarding/${item.id}/reverse-sync`);
                        messageApi.success('已从钉钉智能人事反向同步员工档案');
                        await load();
                      });
                    }}
                    loading={actionLoading === `reverse-${item.id}`}
                  >
                    反向同步档案
                  </Button>
                  <WorkflowStageTransfer
                    offerId={item.offerId}
                    currentStage={materialWorkflowStage(item)}
                    onTransferred={load}
                  />
                </Space>
              );
            }
          }
        ]}
      />
      <OnboardingManagementModal
        detail={active}
        action={action}
        onClose={() => setActive(null)}
        onProvisionAccount={async (id) => {
          await runMaterialAction(`modal-account-${id}`, '开通账号', async () => {
            if (!active || active.id !== id) throw new Error('未找到当前员工信息，请重新打开办理窗口');
            messageApi.success(await provisionOnboardingAccount(active));
            await reloadActive(id);
          });
        }}
        onProvisionEmail={async (id) => {
          await runMaterialAction(`modal-email-${id}`, '开通邮箱', async () => {
            if (!active || active.id !== id) throw new Error('未找到当前员工信息，请重新打开办理窗口');
            setEmailCreation(await provisionOnboardingEmail(active));
            await reloadActive(id);
          });
        }}
        provisioningAccount={actionLoading === `modal-account-${active?.id ?? ''}`}
        provisioningEmail={actionLoading === `modal-email-${active?.id ?? ''}`}
        onSendCompletionInvitation={async (id) => {
          const response = await apiPost<OnboardingCompletionInvitationResult>(
            `/hr/onboarding/${id}/send-completion-invitation`
          );
          messageApi.success('已发送入职登记完善邀请');
          return response;
        }}
      />
    </section>
  );
}

function OnboardingManagementModal({
  detail,
  action,
  onClose,
  onProvisionAccount,
  onProvisionEmail,
  onSendCompletionInvitation,
  provisioningAccount,
  provisioningEmail
}: {
  detail: Material | null;
  action: OnboardingAction;
  onClose: () => void;
  onProvisionAccount: (id: string) => Promise<void>;
  onProvisionEmail: (id: string) => Promise<void>;
  onSendCompletionInvitation: (id: string) => Promise<OnboardingCompletionInvitationResult>;
  provisioningAccount: boolean;
  provisioningEmail: boolean;
}) {
  const [entry, setEntry] = useState<OnboardingCompletionEntry | null>(null);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryError, setEntryError] = useState('');
  const [sendingEntryInvitation, setSendingEntryInvitation] = useState(false);
  useEffect(() => {
    if (!detail || action !== 'entry') {
      setEntry(null);
      setEntryError('');
      return;
    }
    setEntryLoading(true);
    setEntryError('');
    void apiGet<OnboardingCompletionEntry>(`/hr/onboarding/${detail.id}/completion-entry`)
      .then(setEntry)
      .catch((error) => setEntryError(humanizeErrorMessage((error as Error).message)))
      .finally(() => setEntryLoading(false));
  }, [action, detail?.id]);
  if (!detail) return null;
  const email = detail.employeeEmail || detail.employeeEmailPreview || '-';
  const title =
    action === 'entry'
      ? '入职登记入口'
      : action === 'account'
        ? '开通钉钉账号'
        : action === 'email'
          ? '开通钉钉邮箱'
          : '员工档案';
  const joinedCompany = hasJoinedDingTalkCompany(detail);
  const emailOpened = isEmployeeEmailOpen(detail);
  const hrmSynced = isHrmSynced(detail);
  return (
    <Modal title={title} open={Boolean(detail)} onCancel={onClose} footer={null} width={980}>
      <Space direction="vertical" className="full" size={20}>
        {action === 'entry' && (
          <section className="onboarding-section">
            <div className="section-heading">
              <Typography.Title level={4}>入职登记表填写入口</Typography.Title>
              {entry && (
                <Space wrap>
                  <Button onClick={() => copy(entry.onboardingUrl)}>复制登记表链接</Button>
                  <Button onClick={() => window.open(entry.onboardingUrl, '_blank', 'noopener,noreferrer')}>
                    打开登记表
                  </Button>
                  <Button
                    type="primary"
                    loading={sendingEntryInvitation}
                    onClick={async () => {
                      try {
                        setSendingEntryInvitation(true);
                        const response = await onSendCompletionInvitation(detail.id);
                        setEntry((current) =>
                          current
                            ? { ...current, onboardingUrl: response.onboardingUrl || current.onboardingUrl }
                            : current
                        );
                      } catch (error) {
                        message.error(`发送邀请失败：${humanizeErrorMessage((error as Error).message)}`);
                      } finally {
                        setSendingEntryInvitation(false);
                      }
                    }}
                  >
                    发送完善邀请
                  </Button>
                </Space>
              )}
            </div>
            {entryLoading && <span>加载中...</span>}
            {entryError && <Alert type="error" message={entryError} />}
            {entry && (
              <Space direction="vertical" className="full" size={16}>
                <SimpleDescription
                  data={{
                    候选人: entry.candidateName,
                    接收邮箱: entry.candidateEmail || '-',
                    邮箱状态: entry.candidateEmailIsVirtual ? '虚拟邮箱，请先补充真实邮箱' : '可发送',
                    档案状态: materialStatusText(entry.materialStatus)
                  }}
                />
                <div className="onboarding-entry-grid">
                  <div className="onboarding-entry-link">
                    <Typography.Title level={5}>登记表链接</Typography.Title>
                    <Typography.Paragraph copyable={{ text: entry.onboardingUrl }}>
                      {entry.onboardingUrl}
                    </Typography.Paragraph>
                  </div>
                  <div className="onboarding-entry-qr">
                    <Typography.Title level={5}>二维码</Typography.Title>
                    <QRCode value={entry.onboardingUrl} size={220} />
                  </div>
                </div>
              </Space>
            )}
          </section>
        )}

        {action === 'account' && (
          <section className="onboarding-section">
            <div className="section-heading">
              <Typography.Title level={4}>信息确认</Typography.Title>
              <Button
                type="primary"
                disabled={joinedCompany}
                loading={provisioningAccount}
                onClick={() => onProvisionAccount(detail.id)}
              >
                {accountActionText(detail)}
              </Button>
            </div>
            <SimpleDescription
              data={{
                姓名: detail.candidateName,
                手机号: detail.mobile,
                员工类型: detail.employeeType,
                部门: displayDepartment(detail.departmentName),
                职位: detail.position,
                预计入职日期: detail.expectedOnboardDate,
                钉钉状态: joinedCompany
                  ? '已加入企业'
                  : isInvitationPending(detail)
                    ? '已发邀请，待员工同意'
                    : '待发邀请',
                钉钉账号: detail.dingTalkUserId || '-',
                企业邮箱状态: emailOpened
                  ? '已开通'
                  : joinedCompany
                    ? '已加入企业，等待钉钉邮箱同步'
                    : '员工加入企业后自动开通',
                企业邮箱: email,
                智能人事档案: hrmSynced
                  ? `已同步（${detail.syncExternalId || detail.dingTalkUserId || '-'}）`
                  : '待同步',
                到岗时间: detail.arrivedAt || '待确认'
              }}
            />
          </section>
        )}

        {action === 'email' && (
          <section className="onboarding-section">
            <div className="section-heading">
              <Typography.Title level={4}>信息确认</Typography.Title>
              <Button
                type="primary"
                disabled={!joinedCompany || emailOpened}
                loading={provisioningEmail}
                onClick={() => onProvisionEmail(detail.id)}
              >
                {emailActionText(detail)}
              </Button>
            </div>
            <SimpleDescription
              data={{
                员工姓名: detail.candidateName,
                自动生成邮箱: email,
                邮箱域名: 'stardust.ai',
                开通状态: detail.employeeEmail ? '已开通' : '待开通'
              }}
            />
          </section>
        )}

        {action === 'material' && (
          <section className="onboarding-section">
            <Typography.Title level={4}>员工档案</Typography.Title>
            <MaterialDetailTabs detail={detail} />
          </section>
        )}
      </Space>
    </Modal>
  );
}

export function OnboardedPeople({
  canManageWorkflow,
  canViewMaterialDetail
}: {
  canManageWorkflow: boolean;
  canViewMaterialDetail: boolean;
}) {
  const [emailCreation, setEmailCreation] = useState<MailboxCreationResponse>();
  const { items, load, error: listError } = useLocalList<Material>('/hr/onboarding');
  const [detail, setDetail] = useAuthorizedSelection<Material>(items);
  const [active, setActive] = useAuthorizedSelection<Material>(items);
  const [action, setAction] = useState<OnboardingAction>('account');
  const [actionLoading, setActionLoading] = useState('');
  const [batchPreview, setBatchPreview] = useState<OnboardedHrmBatchPreview | null>(null);
  const [batchResult, setBatchResult] = useState<OnboardedHrmBatchResult | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [messageApi, holder] = message.useMessage();
  const reloadActive = async (id: string) => {
    setActive(await apiGet<Material>(`/hr/onboarding/${id}`));
    await load();
  };
  const openAction = async (item: Material, nextAction: 'account' | 'email') => {
    const data = await apiGet<Material>(`/hr/onboarding/${item.id}`);
    setAction(nextAction);
    setActive(data);
  };
  const runAction = async (loadingKey: string, actionName: string, task: () => Promise<void>) => {
    try {
      setActionLoading(loadingKey);
      await task();
    } catch (error) {
      messageApi.error(`${actionName}失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setActionLoading('');
    }
  };
  const openHrmBatchPreview = async () => {
    try {
      setBatchLoading(true);
      setBatchResult(null);
      setBatchPreview(await apiGet<OnboardedHrmBatchPreview>('/hr/onboarding/hrm-sync/batch/preview'));
    } catch (error) {
      messageApi.error(`批量同步预览失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setBatchLoading(false);
    }
  };
  const runHrmBatch = async () => {
    if (!batchPreview?.items.length) return;
    try {
      setBatchLoading(true);
      const result = await apiPost<OnboardedHrmBatchResult>('/hr/onboarding/hrm-sync/batch', {
        confirmed: true,
        materialIds: batchPreview.items.map((item) => item.materialId)
      });
      setBatchResult(result);
      messageApi.success(`智能人事批量同步完成：成功 ${result.success} 人，失败 ${result.failed} 人`);
      await load();
    } catch (error) {
      messageApi.error(`批量同步失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setBatchLoading(false);
    }
  };
  const rows = items.filter((item) => isOnboardedPeopleItem(item));
  return (
    <section>
      {holder}
      <MailboxCreationDialog response={emailCreation} onClose={() => setEmailCreation(undefined)} />
      <PageTitle
        title="已入职人员"
        extra={
          canManageWorkflow ? (
            <Button type="primary" loading={batchLoading && !batchPreview} onClick={() => void openHrmBatchPreview()}>
              批量同步智能人事档案
            </Button>
          ) : undefined
        }
      />
      <FilteredTable
        config={personnelConfig('onboarded')}
        error={listError}
        project={projectMaterialList}
        onQueryChange={() => {
          setBatchPreview(null);
          setBatchResult(null);
        }}
        className="data-table"
        rowKey="id"
        dataSource={rows}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', width: 120, render: (value) => renderTableText(value || '-') },
          { title: '手机号', dataIndex: 'mobile', width: 140, render: (value) => renderTableText(value || '-') },
          {
            title: '员工类型',
            dataIndex: 'employeeType',
            width: 110,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value || '-'))
          },
          { title: '职位', dataIndex: 'position', width: 220, render: (value) => renderTableText(value || '-') },
          {
            title: '预计入职日期',
            dataIndex: 'expectedOnboardDate',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '实际入职日期',
            dataIndex: 'arrivedAt',
            width: 140,
            render: (value) => renderTableText(formatDateOnly(value))
          },
          {
            title: '提交办理时间',
            dataIndex: 'hrOnboardingSubmittedAt',
            width: 190,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '当前审批节点',
            dataIndex: 'approvalCurrentNodeName',
            width: 160,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '当前办理人',
            dataIndex: 'approvalCurrentHandlerName',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '审批状态',
            width: 140,
            render: (_, item) => (
              <Tag color={item.approvalStatus === 'finish' ? 'green' : 'blue'}>{approvalProgressText(item)}</Tag>
            )
          },
          {
            title: '账号/邮箱/智能人事',
            width: 260,
            render: (_, item) => (
              <Space direction="vertical" size={0}>
                {dingTalkAccountDisplay(item)}
                {employeeEmailDisplay(item)}
                {hrmSyncDisplay(item)}
              </Space>
            )
          },
          {
            title: '账号/邮箱办理',
            width: 210,
            render: (_, item) => {
              const joinedCompany = hasJoinedDingTalkCompany(item);
              const emailOpened = isEmployeeEmailOpen(item);
              return (
                <Space direction="vertical" size={8}>
                  <Button disabled={joinedCompany} onClick={() => openAction(item, 'account')}>
                    {accountActionText(item)}
                  </Button>
                  <Button disabled={!joinedCompany || emailOpened} onClick={() => openAction(item, 'email')}>
                    {emailActionText(item)}
                  </Button>
                </Space>
              );
            }
          },
          {
            title: '操作',
            width: 300,
            render: (_, item) => (
              <Space wrap>
                {canViewMaterialDetail && (
                  <Button
                    onClick={async () => {
                      const data = await apiGet<Material>(`/hr/onboarding/${item.id}`);
                      setDetail(data);
                    }}
                  >
                    查看档案
                  </Button>
                )}
                {canManageWorkflow ? (
                  <Button
                    disabled={!item.approvalInstanceId || item.approvalStatus === 'finish'}
                    onClick={async () => {
                      await apiPost(`/approvals/${item.approvalInstanceId}/complete-post-onboarding`);
                      messageApi.success('已模拟行政盖章完成，审批状态已更新为完成');
                      load();
                    }}
                  >
                    模拟行政盖章完成
                  </Button>
                ) : (
                  <Typography.Text type="secondary">仅可查看进度</Typography.Text>
                )}
                <WorkflowStageTransfer
                  offerId={item.offerId}
                  currentStage={materialWorkflowStage(item)}
                  onTransferred={load}
                />
              </Space>
            )
          }
        ]}
      />
      <MaterialDetailModal detail={detail} onClose={() => setDetail(null)} />
      {batchPreview && (
        <Modal
          title="批量同步智能人事档案"
          open={Boolean(batchPreview)}
          width={900}
          maskClosable={false}
          closable={!batchLoading}
          onCancel={() => {
            if (batchLoading) return;
            setBatchPreview(null);
            setBatchResult(null);
          }}
          footer={
            batchResult
              ? [
                  <Button
                    key="close"
                    onClick={() => {
                      setBatchPreview(null);
                      setBatchResult(null);
                    }}
                  >
                    关闭
                  </Button>
                ]
              : [
                  <Button key="cancel" disabled={batchLoading} onClick={() => setBatchPreview(null)}>
                    取消
                  </Button>,
                  <Button
                    key="confirm"
                    type="primary"
                    danger
                    loading={batchLoading}
                    disabled={!batchPreview?.items.length}
                    onClick={() => void runHrmBatch()}
                  >
                    确认批量同步
                  </Button>
                ]
          }
        >
          {batchPreview && !batchResult && (
            <Space direction="vertical" size="middle" className="full">
              <Alert
                type="warning"
                showIcon
                message={`即将正向写入 ${batchPreview.items.length} 名已入职员工的钉钉智能人事档案`}
                description="此操作会按审批档案覆盖钉钉员工类型、职级及其他已启用映射；实习生将写为实习。系统逐人执行，单人失败不会中断整批。"
              />
              {batchPreview.total > batchPreview.maxItems && (
                <Alert
                  type="error"
                  showIcon
                  message={`符合条件员工共 ${batchPreview.total} 人，单批最多 ${batchPreview.maxItems} 人`}
                  description="请联系管理员分批处理，系统不会绕过调用上限。"
                />
              )}
              <FilteredTable
                config={adminConfig('batch', 'preview')}
                project={(row, index) => projectAdmin('batch', row, index)}
                size="small"
                rowKey="materialId"
                pagination={false}
                dataSource={batchPreview.items}
                columns={[
                  { title: '员工', dataIndex: 'employeeName' },
                  { title: '员工类型', dataIndex: 'employeeType' },
                  {
                    title: '钉钉账号',
                    dataIndex: 'hasDingTalkUserId',
                    render: (value: boolean) => (
                      <Tag color={value ? 'green' : 'orange'}>{value ? '已有 userId' : '缺少 userId'}</Tag>
                    )
                  },
                  { title: '本地状态', dataIndex: 'status' }
                ]}
              />
            </Space>
          )}
          {batchResult && (
            <Space direction="vertical" size="middle" className="full">
              <Alert
                type={batchResult.failed ? 'warning' : 'success'}
                showIcon
                message={`成功 ${batchResult.success} 人，失败 ${batchResult.failed} 人，跳过 ${batchResult.skipped} 人`}
              />
              <FilteredTable
                config={adminConfig('batch', 'result')}
                project={(row, index) => projectAdmin('batch', row, index)}
                size="small"
                rowKey="materialId"
                pagination={false}
                dataSource={batchResult.items}
                columns={[
                  { title: '员工', dataIndex: 'employeeName' },
                  { title: '员工类型', dataIndex: 'employeeType' },
                  {
                    title: '结果',
                    dataIndex: 'status',
                    render: (value: OnboardedHrmBatchResultItem['status']) => (
                      <Tag color={value === 'SUCCESS' ? 'green' : value === 'FAILED' ? 'red' : 'default'}>
                        {value === 'SUCCESS' ? '成功' : value === 'FAILED' ? '失败' : '跳过'}
                      </Tag>
                    )
                  },
                  { title: '错误', dataIndex: 'errorMessage', render: (value) => value || '-' }
                ]}
              />
            </Space>
          )}
        </Modal>
      )}
      <OnboardingManagementModal
        detail={active}
        action={action}
        onClose={() => setActive(null)}
        onProvisionAccount={async (id) => {
          await runAction(`modal-account-${id}`, '开通账号', async () => {
            if (!active || active.id !== id) throw new Error('未找到当前员工信息，请重新打开办理窗口');
            messageApi.success(await provisionOnboardingAccount(active));
            await reloadActive(id);
          });
        }}
        onProvisionEmail={async (id) => {
          await runAction(`modal-email-${id}`, '开通邮箱', async () => {
            if (!active || active.id !== id) throw new Error('未找到当前员工信息，请重新打开办理窗口');
            setEmailCreation(await provisionOnboardingEmail(active));
            await reloadActive(id);
          });
        }}
        provisioningAccount={actionLoading === `modal-account-${active?.id ?? ''}`}
        provisioningEmail={actionLoading === `modal-email-${active?.id ?? ''}`}
        onSendCompletionInvitation={async (id) =>
          apiPost<OnboardingCompletionInvitationResult>(`/hr/onboarding/${id}/send-completion-invitation`)
        }
      />
    </section>
  );
}

function CancelledOnboardingPeople({ canManageSensitive }: { canManageSensitive: boolean }) {
  const { items, load, error: listError } = useLocalList<Material>('/hr/onboarding');
  const [detail, setDetail] = useAuthorizedSelection<Material>(items);
  const rows = items.filter((item) => isCancelledOnboardingItem(item));
  return (
    <section>
      <PageTitle title="取消入职人员" />
      <FilteredTable
        config={personnelConfig('cancelled')}
        error={listError}
        project={projectMaterialList}
        rowKey="id"
        dataSource={rows}
        scroll={{ x: 1100 }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', render: (value) => value || '-' },
          { title: '手机号', dataIndex: 'mobile', render: (value) => value || '-' },
          { title: '员工类型', dataIndex: 'employeeType', render: (value) => value || '-' },
          { title: '部门', dataIndex: 'departmentName', render: (value) => displayDepartment(value || '-') },
          { title: '职位', dataIndex: 'position', render: (value) => value || '-' },
          { title: '预计入职日期', dataIndex: 'expectedOnboardDate', render: (value) => value || '-' },
          {
            title: 'Offer状态',
            dataIndex: 'offerStatus',
            render: (status) => <Tag>{offerStatusText(status || '-')}</Tag>
          },
          { title: '档案状态', dataIndex: 'status', render: (status) => <Tag>{materialStatusText(status)}</Tag> },
          {
            title: '操作',
            render: (_, item) => (
              <Space wrap>
                {canManageSensitive ? (
                  <Button
                    onClick={async () => {
                      if (item.id.startsWith('offer-')) {
                        setDetail(item);
                        return;
                      }
                      const data = await apiGet<Material>(`/hr/onboarding/${item.id}`);
                      setDetail(data);
                    }}
                  >
                    查看
                  </Button>
                ) : (
                  <Typography.Text type="secondary">仅可查看进度</Typography.Text>
                )}
                <WorkflowStageTransfer
                  offerId={item.offerId}
                  currentStage={materialWorkflowStage(item)}
                  onTransferred={load}
                />
              </Space>
            )
          }
        ]}
      />
      <MaterialDetailModal detail={detail} onClose={() => setDetail(null)} />
    </section>
  );
}

function MaterialDetailModal({ detail, onClose }: { detail: Material | null; onClose: () => void }) {
  if (!detail) return null;
  return (
    <Modal title="员工档案详情" open={Boolean(detail)} onCancel={onClose} footer={null} width={900}>
      {detail && <MaterialDetailTabs detail={detail} />}
    </Modal>
  );
}

export function WorkflowArchive() {
  const { items, load, error: listError } = useLocalList<ArchiveOffer>('/offers/archive/all');
  return (
    <section>
      <PageTitle title="流程归档" />
      <FilteredTable
        config={personnelConfig('archive')}
        error={listError}
        project={(row, index) =>
          projectPersonnel(row, index, {
            status: offerStatusText(row.status),
            stage: workflowStageText(offerWorkflowStage(row))
          })
        }
        className="data-table"
        rowKey="id"
        dataSource={items}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '候选人', dataIndex: 'candidateName', width: 130, render: renderNameText },
          {
            title: '当前环节',
            width: 130,
            render: (_, offer) => <Tag>{workflowStageText(offerWorkflowStage(offer))}</Tag>
          },
          {
            title: '备注',
            width: 140,
            render: (_, offer) => renderTableText(workflowArchiveRemarkText(offer))
          },
          {
            title: 'Offer状态',
            dataIndex: 'status',
            width: 130,
            render: (value) => <Tag>{offerStatusText(value)}</Tag>
          },
          {
            title: '档案状态',
            dataIndex: 'materialStatus',
            width: 130,
            render: (value) => <Tag>{value === 'NOT_STARTED' ? '未创建' : materialStatusText(value)}</Tag>
          },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value || '-'))
          },
          { title: '职位', dataIndex: 'position', width: 200, render: (value) => renderTableText(value || '-') },
          {
            title: '预计入职日期',
            dataIndex: 'expectedOnboardDate',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '实际入职日期',
            dataIndex: 'arrivedAt',
            width: 140,
            render: (value) => renderTableText(formatDateOnly(value))
          },
          {
            title: '审批进度',
            width: 160,
            render: (_, offer) => <Tag>{approvalProgressText(offer)}</Tag>
          },
          {
            title: '操作',
            width: 130,
            render: (_, offer) => (
              <WorkflowStageTransfer offerId={offer.id} currentStage={offerWorkflowStage(offer)} onTransferred={load} />
            )
          }
        ]}
      />
    </section>
  );
}

export function WorkflowStageTransfer({
  offerId,
  currentStage,
  onTransferred
}: {
  offerId: string;
  currentStage: WorkflowStage | 'CANCELLED';
  onTransferred: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [targetStage, setTargetStage] = useState<WorkflowTransferSelection | undefined>();
  const [loading, setLoading] = useState(false);
  const [messageApi, holder] = message.useMessage();
  const options: WorkflowTransferSelection[] = [
    'PENDING_OFFER',
    'SENT_OFFER',
    'PENDING_ONBOARDING',
    'ONBOARDED',
    'ARCHIVED',
    'CANCEL_ONBOARDING'
  ];
  const isCancellingOnboarding = targetStage === 'CANCEL_ONBOARDING';
  const close = () => {
    if (loading) return;
    setOpen(false);
    setTargetStage(undefined);
  };
  return (
    <>
      {holder}
      <Button onClick={() => setOpen(true)}>转移环节</Button>
      <Modal
        title="转移候选人环节"
        open={open}
        okText={isCancellingOnboarding ? '确认取消入职' : '确认转移'}
        cancelText="取消"
        confirmLoading={loading}
        okButtonProps={{ disabled: !targetStage || targetStage === currentStage, danger: isCancellingOnboarding }}
        onCancel={close}
        onOk={async () => {
          if (!targetStage || targetStage === currentStage) return;
          try {
            setLoading(true);
            await apiPost(`/offers/${offerId}/transfer-stage`, workflowTransferRequest(targetStage));
            messageApi.success(
              isCancellingOnboarding ? '已取消入职并归档' : `已转移到${workflowTransferSelectionText(targetStage)}`
            );
            setOpen(false);
            setTargetStage(undefined);
            await onTransferred();
          } catch (error) {
            messageApi.error(`转移失败：${humanizeErrorMessage((error as Error).message)}`);
          } finally {
            setLoading(false);
          }
        }}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type={isCancellingOnboarding ? 'warning' : 'info'}
            showIcon
            message={
              isCancellingOnboarding
                ? '确认后候选人将进入流程归档，并备注“取消入职”；不会清除档案、附件和历史数据。'
                : '转移只改变候选人所在页面，不发送邮件、不确认到岗、不开通账号、不触发钉钉同步，也不会清除档案和历史数据。'
            }
          />
          <Select
            aria-label="目标环节"
            placeholder="选择目标环节"
            value={targetStage}
            onChange={(value) => setTargetStage(value)}
            style={{ width: '100%' }}
            options={options.map((stage) => ({
              value: stage,
              label: workflowTransferSelectionText(stage),
              disabled: stage === currentStage
            }))}
          />
        </Space>
      </Modal>
    </>
  );
}

export function SignatureDocumentsPage() {
  const { items, load, loading, error: listError } = useLocalList<Material>('/hr/onboarding');
  const [active, setActive] = useAuthorizedSelection<Material>(items);
  const [openingId, setOpeningId] = useState('');
  const [messageApi, holder] = message.useMessage();

  const rows = items
    .filter((item) => !item.id.startsWith('offer-'))
    .sort((left, right) =>
      String(right.expectedOnboardDate ?? '').localeCompare(String(left.expectedOnboardDate ?? ''))
    );

  const openDocuments = async (item: Material) => {
    try {
      setOpeningId(item.id);
      setActive(await apiGet<Material>(`/hr/onboarding/${item.id}`));
    } catch (error) {
      messageApi.error(`读取档案失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setOpeningId('');
    }
  };

  return (
    <section>
      {holder}
      <PageTitle
        title="签署文件"
        extra={
          <Button loading={loading} onClick={() => void load()}>
            刷新本地数据
          </Button>
        }
      />
      <FilteredTable
        config={personnelConfig('signature')}
        error={listError}
        project={projectMaterialList}
        className="data-table"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '员工', dataIndex: 'candidateName', width: 120, render: (value) => renderTableText(value || '-') },
          {
            title: '员工类型',
            dataIndex: 'employeeType',
            width: 100,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '部门',
            dataIndex: 'departmentName',
            width: 180,
            render: (value) => renderTableText(displayDepartment(value || '-'))
          },
          { title: '职位', dataIndex: 'position', width: 180, render: (value) => renderTableText(value || '-') },
          {
            title: '预计入职日期',
            dataIndex: 'expectedOnboardDate',
            width: 140,
            render: (value) => renderTableText(value || '-')
          },
          {
            title: '实际入职日期',
            dataIndex: 'arrivedAt',
            width: 140,
            render: (value) => renderTableText(formatDateOnly(value))
          },
          {
            title: '档案状态',
            dataIndex: 'status',
            width: 150,
            render: (value) => <Tag>{materialStatusText(value)}</Tag>
          },
          {
            title: '文件状态',
            width: 130,
            render: (_, item) => signatureDocumentStatus(item)
          },
          {
            title: '最近生成时间',
            width: 190,
            render: (_, item) => renderTableText(latestSignatureDocumentTime(item))
          },
          {
            title: '操作',
            width: 120,
            render: (_, item) => (
              <Button loading={openingId === item.id} onClick={() => void openDocuments(item)}>
                生成/下载
              </Button>
            )
          }
        ]}
      />
      <Modal
        key={active?.id || 'closed-signature'}
        className="signature-document-workspace-modal"
        title={active ? `${active.candidateName || '员工'} - 签署文件` : '签署文件'}
        open={Boolean(active)}
        onCancel={() => setActive(null)}
        footer={null}
        width={1080}
      >
        {active && <SignatureDocumentsPanel detail={active} />}
      </Modal>
    </section>
  );
}

function signatureDocumentStatus(item: Material) {
  if (!['SUBMITTED', 'APPROVED', 'SYNCED'].includes(item.status)) return <Tag>档案未提交</Tag>;
  const count = item.generatedDocuments?.length ?? 0;
  return count > 0 ? <Tag color="green">已生成 {count} 份</Tag> : <Tag color="orange">待生成</Tag>;
}

function latestSignatureDocumentTime(item: Material) {
  const latest = [...(item.generatedDocuments ?? [])].sort((left, right) =>
    right.generatedAt.localeCompare(left.generatedAt)
  )[0]?.generatedAt;
  return latest ? new Date(latest).toLocaleString('zh-CN', { hour12: false }) : '-';
}

function SignatureDocumentsPanel({ detail }: { detail: Material }) {
  const [preview, setPreview] = useState<OnboardingDocumentPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [messageApi, holder] = message.useMessage();

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiPost<OnboardingDocumentPreview>(
        `/hr/onboarding/${detail.id}/signature-documents/preview`
      );
      setPreview(result);
    } catch (nextError) {
      setError(humanizeErrorMessage((nextError as Error).message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPreview();
  }, [detail.id]);

  const generate = async () => {
    try {
      setGenerating(true);
      const result = await apiPost<{ documents: GeneratedOnboardingDocument[] }>(
        `/hr/onboarding/${detail.id}/signature-documents/generate`
      );
      setPreview((current) => (current ? { ...current, generatedDocuments: result.documents } : current));
      await loadPreview();
      messageApi.success('签署文件已生成并保存');
    } catch (nextError) {
      await loadPreview();
      messageApi.error(`生成失败：${humanizeErrorMessage((nextError as Error).message)}`);
    } finally {
      setGenerating(false);
    }
  };

  const download = async (document: GeneratedOnboardingDocument) => {
    try {
      const result = await apiDownload(
        `/hr/onboarding/${detail.id}/signature-documents/${document.kind}/download`,
        document.fileName
      );
      const url = window.URL.createObjectURL(result.blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = result.fileName;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (nextError) {
      messageApi.error(`下载失败：${humanizeErrorMessage((nextError as Error).message)}`);
    }
  };

  return (
    <section className="signature-document-panel">
      {holder}
      <div className="section-heading">
        <Typography.Title level={4}>入职签署文件</Typography.Title>
        <Button loading={loading} onClick={() => void loadPreview()}>
          刷新本地数据
        </Button>
      </div>
      {error && <Alert type="error" showIcon message={error} />}
      {loading && !preview && <Typography.Text type="secondary">正在读取已提交的员工档案...</Typography.Text>}
      {preview && (
        <Space direction="vertical" size={16} className="full">
          <Alert
            type="info"
            showIcon
            message="仅供 HR 下载后线下签署，候选人端不可见"
            description="生成前请核对档案和录用审批数据；签字位置、缺失字段和无法自动核验的邮编保持空白。系统会自动检索邮编，查询失败时仍可生成文件，请在下载后手工填写。"
          />
          <SimpleDescription
            data={{
              员工姓名: preview.data.name,
              员工类型: preview.data.employeeType,
              部门: preview.data.department,
              职位: preview.data.position,
              身份证地址: preview.data.idAddress,
              现居住地址: preview.data.currentAddress,
              合同开始日: preview.data.contractStartDate,
              合同到期日: preview.data.contractEndDate,
              试用期结束日: preview.data.probationEndDate,
              ...(preview.data.employeeType === '实习'
                ? {
                    实习结束日: preview.data.internshipEndDate,
                    实习日薪: preview.data.internDailyRate
                  }
                : {}),
              工作地点: preview.data.workLocation
            }}
          />
          <div className="signature-document-fields">
            <PostalCodeResolutionView label="身份证地址邮编" result={preview.postal.idAddress} />
            <PostalCodeResolutionView label="现居住地址邮编" result={preview.postal.currentAddress} />
          </div>
          <Space wrap>
            <Button
              type="primary"
              loading={generating}
              disabled={!['SUBMITTED', 'APPROVED', 'SYNCED'].includes(detail.status)}
              onClick={() => void generate()}
            >
              生成签署文件
            </Button>
            <Typography.Text type="secondary">
              {preview.documents.map((document) => `${document.name} ${document.format}`).join('、')}
            </Typography.Text>
          </Space>
          {!['SUBMITTED', 'APPROVED', 'SYNCED'].includes(detail.status) && (
            <Alert type="warning" showIcon message="员工档案尚未提交，暂不能生成签署文件" />
          )}
          {preview.generatedDocuments.length > 0 && (
            <div className="signature-document-list">
              <Typography.Text strong>已生成文件</Typography.Text>
              {preview.generatedDocuments.map((document) => (
                <div className="signature-document-row" key={document.kind}>
                  <div>
                    <Typography.Text>{document.fileName}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary">
                      {new Date(document.generatedAt).toLocaleString('zh-CN', { hour12: false })} /{' '}
                      {document.generatedBy}
                    </Typography.Text>
                  </div>
                  <Button onClick={() => void download(document)}>下载</Button>
                </div>
              ))}
            </div>
          )}
        </Space>
      )}
    </section>
  );
}

function PostalCodeResolutionView({ label, result }: { label: string; result: PostalCodeResolution }) {
  const resolved = result.status === 'RESOLVED';
  const notRequired = result.status === 'NOT_REQUIRED';
  const pending = result.status === 'LOOKUP_REQUIRED';
  return (
    <div className="postal-code-resolution">
      <div className="postal-code-resolution-heading">
        <Typography.Text strong>{label}</Typography.Text>
        <Tag color={resolved ? 'green' : pending ? 'blue' : notRequired ? 'default' : 'red'}>
          {resolved ? '已自动核验' : pending ? '待自动检索' : notRequired ? '地址为空' : '无法确定'}
        </Tag>
      </div>
      {resolved ? (
        <>
          <Typography.Title level={5}>{result.code}</Typography.Title>
          <Typography.Text type="secondary">数据来源：{result.source}</Typography.Text>
          {result.verifiedAt && (
            <Typography.Text type="secondary">
              核验时间：{new Date(result.verifiedAt).toLocaleString('zh-CN', { hour12: false })}
            </Typography.Text>
          )}
        </>
      ) : (
        <Typography.Text type={notRequired || pending ? 'secondary' : 'danger'}>
          {result.message || '没有唯一且可核验的邮编，文件仍会生成，请手工填写'}
        </Typography.Text>
      )}
    </div>
  );
}

export function MaterialDetailTabs({ detail }: { detail: Material; canManageDocuments?: boolean }) {
  return (
    <Tabs
      items={[
        {
          key: 'basic',
          label: '基础信息',
          children: (
            <SimpleDescription
              data={{
                候选人: detail.candidateName,
                手机号: detail.mobile,
                员工类型: detail.employeeType,
                部门: displayDepartment(detail.departmentName),
                职位: detail.position,
                预计入职日期: detail.expectedOnboardDate,
                Offer状态: offerStatusText(detail.offerStatus || '-'),
                档案状态: materialStatusText(detail.status),
                ...detail.formData
              }}
            />
          )
        },
        {
          key: 'education',
          label: '学历',
          children: <SimpleTable title="" page="education" scope={detail.id} items={detail.educations ?? []} />
        },
        {
          key: 'work-experience',
          label: '工作经历',
          children: <SimpleTable title="" page="experience" scope={detail.id} items={detail.workExperiences ?? []} />
        },
        {
          key: 'contacts',
          label: '联系人',
          children: <SimpleTable title="" page="contacts" scope={detail.id} items={detail.emergencyContacts ?? []} />
        },
        {
          key: 'attachments',
          label: '附件',
          children: <AttachmentTable detail={detail} />
        },
        {
          key: 'sync',
          label: '到岗同步',
          children: (
            <SimpleDescription
              data={{
                arrivedAt: detail.arrivedAt,
                dingTalkUserId: detail.dingTalkUserId,
                employeeEmail: detail.employeeEmail,
                employeeEmailPreview: detail.employeeEmailPreview,
                syncExternalId: detail.syncExternalId,
                errorMessage: detail.errorMessage,
                accountProvisionResult: detail.accountProvisionResult
              }}
            />
          )
        }
      ]}
    />
  );
}

function RiskList() {
  const { items, error } = useLocalList<Record<string, unknown>>('/hr/risks');
  return <SimpleTable title="异常提醒" page="risks" items={items} error={error} />;
}

function SmartHrmMappingPage() {
  const [mappings, setMappings] = useState<Record<string, unknown>[]>([]);
  const [rules, setRules] = useState<Record<string, unknown>[]>([]);
  const [hrmMappings, setHrmMappings] = useState<Record<string, unknown>[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [mappingLoadError, setMappingLoadError] = useState('');
  const [syncingHrmMetadata, setSyncingHrmMetadata] = useState(false);
  const [hrmSyncResult, setHrmSyncResult] = useState<Record<string, unknown> | null>(null);
  const loadHrmMappings = async () => {
    const items = await apiGet<Record<string, unknown>[]>('/admin/dingtalk-hrm-field-mappings');
    setHrmMappings(items);
    return items;
  };
  useEffect(() => {
    setLoadingMappings(true);
    setMappingLoadError('');
    Promise.all([
      apiGet<Record<string, unknown>[]>('/admin/field-mappings'),
      apiGet<Record<string, unknown>[]>('/admin/template-rules'),
      apiGet<Record<string, unknown>[]>('/admin/dingtalk-hrm-field-mappings')
    ])
      .then(([fieldItems, ruleItems, hrmItems]) => {
        setMappings(fieldItems);
        setRules(ruleItems);
        setHrmMappings(hrmItems);
        if (hrmItems.length === 0) setMappingLoadError('智能人事映射数据为空，请联系管理员检查持久化状态。');
      })
      .catch((error) => {
        setMappingLoadError(error instanceof Error ? error.message : '智能人事映射加载失败');
      })
      .finally(() => setLoadingMappings(false));
  }, []);
  const syncHrmMetadata = async () => {
    setSyncingHrmMetadata(true);
    try {
      const result = await apiPost<Record<string, unknown>>('/admin/dingtalk-hrm-field-mappings/sync-metadata');
      setHrmSyncResult(result);
      await loadHrmMappings();
      message.success('已同步钉钉智能人事字段元数据');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '同步钉钉字段元数据失败');
    } finally {
      setSyncingHrmMetadata(false);
    }
  };
  return (
    <section>
      <PageTitle title="智能人事映射" />
      {mappingLoadError && <Alert type="error" showIcon message="映射加载失败" description={mappingLoadError} />}
      <Typography.Title level={4}>审批字段映射</Typography.Title>
      <FilteredTable
        config={adminConfig('approval-fields')}
        project={(row, index) => projectAdmin('approval-fields', row, index)}
        className="data-table"
        rowKey="id"
        dataSource={mappings}
        pagination={false}
        loading={loadingMappings}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '审批字段', dataIndex: 'approvalFieldLabel', width: 220 },
          { title: '系统字段', dataIndex: 'targetField', width: 220 },
          { title: '数据类型', dataIndex: 'type', width: 120, render: (value) => String(value ?? '-') },
          { title: '必填', dataIndex: 'required', width: 90, render: (value) => (value ? '是' : '否') },
          { title: '启用', dataIndex: 'enabled', width: 90, render: (value) => (value ? '是' : '否') }
        ]}
      />
      <Typography.Title level={4}>Offer模板规则</Typography.Title>
      <FilteredTable
        config={adminConfig('template-rules')}
        project={(row, index) => projectAdmin('template-rules', row, index)}
        className="data-table"
        rowKey="id"
        dataSource={rules}
        pagination={false}
        loading={loadingMappings}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '规则名称', dataIndex: 'name', width: 180 },
          { title: '员工类型', dataIndex: 'employeeTypes', width: 180, render: (value) => renderTableText(value) },
          { title: '部门', dataIndex: 'departments', width: 220, render: (value) => renderTableText(value) },
          { title: '模板ID', dataIndex: 'templateId', width: 180 },
          { title: '优先级', dataIndex: 'priority', width: 90 },
          { title: '启用', dataIndex: 'enabled', width: 90, render: (value) => (value ? '是' : '否') }
        ]}
      />
      <Space className="section-toolbar" align="center" wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          智能人事映射模板（系统字段 - 钉钉智能人事）
        </Typography.Title>
        <Button type="primary" loading={syncingHrmMetadata} onClick={syncHrmMetadata}>
          同步钉钉字段元数据
        </Button>
      </Space>
      <Alert
        type="info"
        message={`当前已加载 ${hrmMappings.length} 条智能人事字段映射。基础字段会根据钉钉元数据自动回填 groupId、sectionIndex 和 fieldCode；员工级同步结果请在“已入职人员”的“智能人事档案”列查看。`}
      />
      {hrmSyncResult && (
        <Alert
          type="success"
          message={`本次识别字段 ${String(hrmSyncResult.totalFields ?? 0)} 个，更新映射 ${String(hrmSyncResult.updatedMappings ?? 0)} 个，附件字段 ${String(hrmSyncResult.attachmentFields ?? 0)} 个。`}
        />
      )}
      <FilteredTable
        config={adminConfig('hrm-fields')}
        project={(row, index) => projectAdmin('hrm-fields', row, index)}
        className="data-table"
        rowKey="id"
        dataSource={hrmMappings}
        pagination={false}
        loading={loadingMappings}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '系统字段', dataIndex: 'name', width: 180 },
          { title: '来源字段', dataIndex: 'sourcePath', width: 260 },
          {
            title: '钉钉分组',
            dataIndex: 'groupName',
            width: 160,
            render: (value, record) => String(value ?? record.groupId ?? '-')
          },
          { title: '分组ID', dataIndex: 'groupId', width: 140 },
          { title: '区块', dataIndex: 'sectionIndex', width: 80 },
          { title: '字段Code', dataIndex: 'fieldCode', width: 220 },
          { title: '类型', dataIndex: 'fieldType', width: 120, render: (value) => String(value ?? '-') },
          {
            title: '状态',
            dataIndex: 'syncStatus',
            width: 150,
            render: (value) => {
              const status = String(value ?? 'READY');
              const color = status === 'READY' ? 'green' : status === 'ATTACHMENT_PENDING' ? 'green' : 'red';
              const label =
                status === 'READY' ? '可同步' : status === 'ATTACHMENT_PENDING' ? '附件可同步' : '缺少元数据';
              return <Tag color={color}>{label}</Tag>;
            }
          },
          {
            title: '启用',
            dataIndex: 'enabled',
            width: 90,
            render: (value) => (value ? <Tag color="blue">启用</Tag> : <Tag>停用</Tag>)
          }
        ]}
      />
    </section>
  );
}

function LogPage() {
  const { items, error } = useLocalList<Record<string, unknown>>('/admin/operation-logs');
  return <SimpleTable title="操作日志" page="logs" items={items} error={error} />;
}

function AttachmentTable({ detail }: { detail: Material }) {
  const [loading, setLoading] = useState('');
  const items = detail.attachments ?? [];
  const keys = Object.keys(items[0] ?? { attachmentType: '', fileName: '', mimeType: '' }).filter(
    (key) => !['fileKey', 'base64', 'contentBase64'].includes(key)
  );
  const download = async (attachment: Record<string, unknown>) => {
    const attachmentType = String(attachment.attachmentType ?? '').trim();
    if (!attachmentType) return;
    try {
      setLoading(attachmentType);
      const fileName = String(attachment.fileName ?? '附件');
      const result = await apiDownload(
        `/hr/onboarding/${detail.id}/attachments/${encodeURIComponent(attachmentType)}/download`,
        fileName
      );
      const url = window.URL.createObjectURL(result.blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = result.fileName;
      anchor.style.display = 'none';
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1_000);
    } catch (error) {
      message.error(`附件下载失败：${humanizeErrorMessage((error as Error).message)}`);
    } finally {
      setLoading('');
    }
  };
  return (
    <FilteredTable
      config={adminConfig('attachments', detail.id)}
      project={(row, index) => projectAdmin('attachments', row, index)}
      className="data-table"
      rowKey={(item) => String(item.fileKey ?? item.attachmentType ?? item.fileName ?? '')}
      dataSource={items}
      tableLayout="auto"
      scroll={{ x: 'max-content' }}
      columns={[
        ...keys.map((key) => ({
          title: key,
          dataIndex: key,
          width: 180,
          render: (value: unknown) =>
            renderTableText(typeof value === 'object' ? JSON.stringify(value) : (value ?? '-'))
        })),
        {
          title: '操作',
          width: 120,
          render: (_: unknown, item: Record<string, unknown>) => (
            <Button
              loading={loading === String(item.attachmentType ?? '')}
              disabled={!item.fileKey}
              onClick={() => void download(item)}
            >
              下载
            </Button>
          )
        }
      ]}
    />
  );
}

function SimpleTable({
  title,
  items,
  page,
  error,
  scope = ''
}: {
  title: string;
  items: Record<string, unknown>[];
  page: AdminPage;
  scope?: string;
  error?: string;
}) {
  return (
    <section>
      {title && <PageTitle title={title} />}
      <FilteredTable
        config={adminConfig(page, scope)}
        error={error}
        project={(row, index) => projectAdmin(page, row, index)}
        className="data-table"
        rowKey={(item, index) => projectAdmin(page, item, index ?? 0).id}
        dataSource={items}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={Object.keys(items[0] ?? { id: '' }).map((key) => ({
          title: key,
          dataIndex: key,
          width: 180,
          render: (value) => renderTableText(typeof value === 'object' ? JSON.stringify(value) : (value ?? '-'))
        }))}
      />
    </section>
  );
}

function SimpleDescription({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data ?? {}).filter(([, value]) => value !== undefined && value !== '');
  if (entries.length === 0) return <Typography.Text type="secondary">暂无数据</Typography.Text>;
  return (
    <Space direction="vertical" className="full">
      {entries.map(([key, value]) => (
        <div key={key} className="detail-row">
          <Typography.Text type="secondary">{key}</Typography.Text>
          <Typography.Text>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography.Text>
        </div>
      ))}
    </Space>
  );
}

function PageTitle({ title, extra }: { title: string; extra?: React.ReactNode }) {
  return (
    <div className="page-title">
      <Typography.Title level={2}>{title}</Typography.Title>
      {extra}
    </div>
  );
}

function statusText(status: string) {
  return (
    {
      ACCEPTED: '该录用通知函已接受',
      REJECTED: '该录用通知函已拒绝，请联系HR',
      EXPIRED: '该录用通知函已失效，请联系HR',
      WITHDRAWN: '该录用通知函已撤回，请联系HR',
      CANCELLED: '该入职流程已取消，请联系HR'
    }[status] ?? '当前状态不可操作'
  );
}

function offerStatusText(status: string) {
  return (
    {
      READY_TO_SEND: '待发offer',
      SENT: '待接受',
      ACCEPTED: '已接受',
      REJECTED: '已拒绝',
      EXPIRED: '已过期',
      WITHDRAWN: '已撤回',
      ARCHIVED: '已过办理节点',
      CANCELLED: '已取消入职'
    }[status] ?? status
  );
}

function offerActionText(status: string) {
  if (status === 'READY_TO_SEND') return '发送Offer';
  if (['SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED'].includes(status)) return '已发送';
  return '查看Offer';
}

function materialStatusText(status: string) {
  return (
    {
      NOT_STARTED: '未开始',
      PENDING_SUBMIT: '待提交入职信息',
      SUBMITTED: '已完成',
      REJECTED: '已驳回',
      APPROVED: '已完成',
      SYNC_FAILED: '智能人事同步失败',
      SYNCED: '已完成'
    }[status] ?? status
  );
}

function projectMaterialList(row: Material, index: number) {
  return projectPersonnel(row, index, {
    status: materialStatusText(row.status),
    account: hasJoinedDingTalkCompany(row) ? '已加入企业' : isInvitationPending(row) ? '待接受邀请' : '未发邀请',
    email: isEmployeeEmailOpen(row) ? '已开通' : getEmployeeEmailResult(row).success === false ? '开通失败' : '待开通',
    hrm: isHrmSynced(row) ? '已同步' : row.status === 'SYNC_FAILED' ? '同步失败' : '待同步',
    documents: !['SUBMITTED', 'APPROVED', 'SYNCED'].includes(row.status)
      ? '档案未提交'
      : row.generatedDocuments?.length
        ? '已生成'
        : '待生成'
  });
}

function readActionMessage(result: Record<string, unknown> | undefined, fallback: string) {
  const message = result?.message;
  return typeof message === 'string' && message ? message : fallback;
}

function getDingTalkAccountResult(item: Material) {
  const result = item.accountProvisionResult?.dingTalk;
  return result && typeof result === 'object' ? (result as Record<string, unknown>) : {};
}

function getEmployeeEmailResult(item: Material) {
  const result = item.accountProvisionResult?.email;
  return result && typeof result === 'object' ? (result as Record<string, unknown>) : {};
}

function getHrmResult(item: Material) {
  const result = item.accountProvisionResult?.hrm ?? item.accountProvisionResult?.hrmPreentry;
  return result && typeof result === 'object' ? (result as Record<string, unknown>) : {};
}

function isInvitationPending(item: Material) {
  const result = getDingTalkAccountResult(item);
  const status = String(result.invitationStatus ?? '');
  const message = String(result.message ?? result.errorMessage ?? '');
  return status === 'PENDING_ACCEPTANCE' || /invitation has been issued/i.test(message) || /邀请.*已.*发/.test(message);
}

function hasJoinedDingTalkCompany(item: Material) {
  return Boolean(item.dingTalkUserId);
}

function isEmployeeEmailOpen(item: Material) {
  if (getEmployeeEmailResult(item).success === false) return false;
  return hasJoinedDingTalkCompany(item) && Boolean(item.employeeEmail);
}

function isHrmSynced(item: Material) {
  return item.status === 'SYNCED' || Boolean(item.syncExternalId);
}

function dingTalkAccountDisplay(item: Material) {
  if (hasJoinedDingTalkCompany(item)) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="green">已加入企业</Tag>
        {renderTableText(item.dingTalkUserId)}
      </Space>
    );
  }
  if (isInvitationPending(item)) return <Tag color="blue">已发邀请，待员工同意</Tag>;
  return <Tag>待发邀请</Tag>;
}

function employeeEmailDisplay(item: Material) {
  const result = getEmployeeEmailResult(item);
  const emailMessage = String(result.message ?? '');
  const email = item.employeeEmail || item.employeeEmailPreview || '-';
  if (isEmployeeEmailOpen(item)) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="green">已开通</Tag>
        {renderTableText(email)}
      </Space>
    );
  }
  if (hasJoinedDingTalkCompany(item)) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="blue">等待邮箱同步</Tag>
        {renderTableText(email)}
        {emailMessage && <Typography.Text type="secondary">{emailMessage}</Typography.Text>}
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={2}>
      <Tag>加入企业后自动开通</Tag>
      {renderTableText(email)}
    </Space>
  );
}

function hrmSyncDisplay(item: Material) {
  const result = getHrmResult(item);
  const resultMessage = String(result.message ?? result.errorMessage ?? item.errorMessage ?? '');
  if (isHrmSynced(item)) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="green">已同步智能人事</Tag>
        {renderTableText(item.syncExternalId || item.dingTalkUserId || '-')}
      </Space>
    );
  }
  if (item.status === 'SYNC_FAILED' || result.errorMessage) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="red">同步失败</Tag>
        {resultMessage && <Typography.Text type="danger">{resultMessage}</Typography.Text>}
      </Space>
    );
  }
  if (result.success) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="blue">已写入智能人事待入职档案</Tag>
        {resultMessage && <Typography.Text type="secondary">{resultMessage}</Typography.Text>}
      </Space>
    );
  }
  if (hasJoinedDingTalkCompany(item)) {
    return (
      <Space direction="vertical" size={2}>
        <Tag color="blue">待同步智能人事</Tag>
        {resultMessage && <Typography.Text type="secondary">{resultMessage}</Typography.Text>}
      </Space>
    );
  }
  return <Tag>加入企业后同步</Tag>;
}

function accountActionText(item: Material) {
  if (hasJoinedDingTalkCompany(item)) return '已加入企业';
  if (isInvitationPending(item)) return '检查加入状态';
  return '发出邀请';
}

function emailActionText(item: Material) {
  if (isEmployeeEmailOpen(item)) return '邮箱已开通';
  if (!hasJoinedDingTalkCompany(item)) return '加入企业后开通邮箱';
  return '开通邮箱';
}

function isPendingOnboardingItem(item: Material) {
  return materialWorkflowStage(item) === 'PENDING_ONBOARDING';
}

function canUseOnboardingCompletionEntry(item: Material) {
  return item.offerStatus === 'ACCEPTED' && ['PENDING_SUBMIT', 'REJECTED'].includes(item.status);
}

function isOnboardedPeopleItem(item: Material) {
  return materialWorkflowStage(item) === 'ONBOARDED';
}

export function isCancelledOnboardingItem(
  item: Pick<
    Material,
    'workflowStage' | 'workflowStageRemark' | 'offerStatus' | 'status' | 'arrivedAt' | 'hrOnboardingSubmittedAt'
  >
) {
  if (item.offerStatus === 'CANCELLED') return true;
  const stage = materialWorkflowStage(item);
  return stage === 'CANCELLED' || (stage === 'ARCHIVED' && item.workflowStageRemark?.trim() === '取消入职');
}

export function offerWorkflowStage(offer: Pick<Offer, 'status' | 'workflowStage' | 'emailSentAt' | 'acceptedAt'>) {
  if (offer.workflowStage) return offer.workflowStage;
  if (offer.status === 'CANCELLED') return 'CANCELLED';
  if (offer.status === 'ARCHIVED') return 'ARCHIVED';
  if (offer.status === 'ACCEPTED' || offer.acceptedAt) return 'PENDING_ONBOARDING';
  if (offer.status === 'SENT' || offer.emailSentAt) return 'SENT_OFFER';
  return 'PENDING_OFFER';
}

export function materialWorkflowStage(
  item: Pick<Material, 'workflowStage' | 'offerStatus' | 'status' | 'arrivedAt' | 'hrOnboardingSubmittedAt'>
) {
  if (item.workflowStage) return item.workflowStage;
  if (item.offerStatus === 'CANCELLED') return 'CANCELLED';
  if (item.offerStatus === 'ARCHIVED') return 'ARCHIVED';
  if (item.arrivedAt || item.hrOnboardingSubmittedAt) return 'ONBOARDED';
  return 'PENDING_ONBOARDING';
}

export function workflowStageText(stage: WorkflowStage | 'CANCELLED') {
  return (
    {
      PENDING_OFFER: '待发Offer',
      SENT_OFFER: '已发Offer',
      PENDING_ONBOARDING: '待入职',
      ONBOARDED: '已入职',
      ARCHIVED: '已归档',
      CANCELLED: '取消入职'
    } as const
  )[stage];
}

export function workflowTransferSelectionText(selection: WorkflowTransferSelection) {
  return selection === 'CANCEL_ONBOARDING' ? '取消入职' : workflowStageText(selection);
}

export function workflowTransferRequest(selection: WorkflowTransferSelection) {
  return selection === 'CANCEL_ONBOARDING'
    ? { stage: 'ARCHIVED' as const, reason: 'CANCEL_ONBOARDING' as const }
    : { stage: selection };
}

export function workflowArchiveRemarkText(offer: { workflowStageRemark?: string }) {
  return offer.workflowStageRemark?.trim() || '-';
}

export function approvalProgressText(item: { approvalStatus?: string; approvalCurrentNodeName?: string }) {
  if (item.approvalStatus === 'finish') return '完成';
  return item.approvalCurrentNodeName || '审批中';
}

export function displayDepartment(value?: string) {
  if (!value) return '-';
  const normalized = value.replaceAll('－', '-').replaceAll('—', '-');
  return normalized.split('-').pop() || value;
}

export function formatDateOnly(value?: string) {
  if (!value) return '-';
  return String(value).slice(0, 10);
}

function copy(text: string) {
  navigator.clipboard.writeText(text);
}

export function mountApp(
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.getElementById('root')
) {
  if (!root) return;
  createRoot(root).render(<App />);
}

mountApp();
